// OrderDetailV3.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Standalone v3 order detail page. Completely independent of OrderDetailV2 —
// no wrapper, no context injection, no !important overrides. Own fetch, own
// layout, own CSS.
//
// Phase 1: full display layout matching the mockup.
//   • Sticky header, notification banner, stages strip, 2-col grid
//   • Fulfillments tab: FO sections, items table, bundle expand/collapse,
//     pick/press/pack dots
//   • Sidebar: Customer, Ship to, Order info, Order note, Tags
//   • Players / Visual timeline / Event log tabs: stubs
//
// Phase 2 (next): pick/press write actions, modify mode, print pipeline,
//   fulfillment dropdown, refund modal.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, useAuthFetch } from '../AuthComponent';
import { useStore } from '../StoreContext';
import { buildBundles, MISSING_IMAGE_SVG } from '../pressingHelpers';
import {
    dotsForBundle, dotsForRow, isDecoration, psForSku,
} from '../order-detail-v2/dotStates';
import { isVirtual } from '../virtualTitles';
import { resolveOrdersBackTo } from '../ordersBackTo';
import OrderPrintCard, {
    isPrinted as isPrintedTag,
    getPerLocationPrintTags,
    buildPopupHTML,
    MergedNjPrint,
} from '../OrderPrintCard';
import NumberPreviewThumb, { buildFontFaceCss, getPreviewFor, getPrintedNumberAttr } from '../NumberPreviewThumb';
import { njMergeAllowed, njWarehouseLocsOnOrder, readMergeNjPref, writeMergeNjPref } from '../constants/njWarehouses';
import { Checkbox, BlockStack, Text, Modal, Select, Banner } from '@shopify/polaris';
import SmartProductImage from '../SmartProductImage';
import { isLabelFirstWarehouse } from '../constants/labelFirstWarehouses';
import {
    JumbleBadge, SkuNotificationCell, SkuNotificationsContext, StoreKeyContext,
    FulfillmentMenu, isPickupMethod, CANCEL_REASON_LABELS,
} from '../OrderDetail';
import { getPreviewImageUrls } from '../previewImage';
import VisualTimelineV3 from './VisualTimelineV3';
import EventLogV3 from './EventLogV3';
import OrderNotificationsControl from '../OrderNotificationsControl';
import RefundModal from '../RefundModal';
import ReportIssueModal from '../ReportIssueModal';
import { hasRole, allRolesIn } from '../constants/roles';
import './order-detail-v3.css';

// ─── helpers (copied from V2 so V3 is self-contained, no V2 import) ───────────

function foPickupStage(fo) {
    if (!fo || fo._warehousePickupOpen) return 0;
    if (isLabelFirstWarehouse(fo?.assignedLocation?.location?.id?.split('/').pop())) return 0;
    const ps = fo._pickupState || {};
    if (ps.dest_is_store === false) return 0;
    const st = ps.effective_state;
    return (typeof st === 'number' && st >= 1) ? Math.min(st, 3) : 0;
}

const PICKUP_STAGE_LABELS = { 1: 'In transit', 2: 'Ready for pickup', 3: 'Picked up' };

function formatFoStatus(subSection) {
    if (!subSection) return '';
    if (subSection.displayMode === 'pending' && subSection.pickupStage >= 1)
        return PICKUP_STAGE_LABELS[subSection.pickupStage] || 'Fulfilled';
    if (subSection.displayMode === 'pending')   return 'Unfulfilled';
    if (subSection.displayMode === 'fulfilled') return 'Fulfilled';
    if (subSection.displayMode === 'refunded')  return 'Refunded';
    const s = (subSection.fo?.status || '').toUpperCase();
    if (s === 'CLOSED')      return 'Fulfilled';
    if (s === 'IN_PROGRESS') return 'In progress';
    if (s === 'OPEN')        return 'Unfulfilled';
    return '';
}

// Which fulfillment orders get a card — same rules as the V1 page: drop stale duplicates
// (older FOs superseded by a newer one for the same line item), voided-label ghosts, and
// "transfer" FOs (delivery method NONE) that only carry fee lines such as Processing Fee or
// Free Returns + Package Protection.
function selectDisplayFOs(order, storedLabels) {
    const fos = (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean);
    const fulfillments = order?.fulfillments || [];
    const lineItems = (order?.lineItems?.edges || []).map(e => e.node).filter(Boolean);
    const foNumId = (fo) => parseInt(String(fo.id).split('/').pop(), 10);
    const liIdsOf = (fo) => (fo.lineItems?.edges || []).map(e => e.node?.lineItem?.id).filter(Boolean);

    const newestFOIdForLineItem = {};
    fos.forEach(fo => {
        const id = foNumId(fo);
        liIdsOf(fo).forEach(liId => {
            if (!newestFOIdForLineItem[liId] || id > newestFOIdForLineItem[liId]) newestFOIdForLineItem[liId] = id;
        });
    });
    const anyFOHasLineItems = fos.some(fo => liIdsOf(fo).length > 0);
    const foHasOwnShipment = (fo) => (fo.fulfillments?.edges || [])
        .some(e => (e.node?.status || '').toUpperCase() === 'SUCCESS');
    const foIsLiveOpen = (fo) => ['OPEN', 'IN_PROGRESS'].includes(fo.status)
        && (fo.lineItems?.edges || []).some(e => (e.node?.remainingQuantity || 0) > 0);
    const visibleFOs = fos.filter(fo => {
        const ids = liIdsOf(fo);
        if (ids.length === 0) return !anyFOHasLineItems;
        if (foIsLiveOpen(fo)) return true;
        if (foHasOwnShipment(fo)) return true;
        return ids.some(liId => newestFOIdForLineItem[liId] === foNumId(fo));
    });
    const isOpenFo = (fo) => fo.status === 'OPEN' || fo.status === 'IN_PROGRESS';
    const sortedFOs = [...visibleFOs.filter(isOpenFo), ...visibleFOs.filter(fo => !isOpenFo(fo))];

    const getFulfillment = (fo) => {
        const ownIds = (fo.fulfillments?.edges || []).map(e => e.node?.id).filter(Boolean);
        if (ownIds.length) {
            const own = fulfillments.filter(f => ownIds.includes(f.id));
            const pick = own.find(f => f.status !== 'CANCELLED') || own[0];
            if (pick) return pick;
        }
        const ids = liIdsOf(fo);
        const matches = fulfillments.filter(f =>
            (f.fulfillmentLineItems?.edges || []).some(e => ids.includes(e.node?.lineItem?.id)));
        return matches.find(f => f.status !== 'CANCELLED') || matches[0] || null;
    };
    const isCancelledLabelFO = (fo) => {
        if (isOpenFo(fo)) return false;
        if (fo.requestStatus !== 'UNSUBMITTED') return false;
        const ids = liIdsOf(fo);
        const matches = fulfillments.filter(f =>
            (f.fulfillmentLineItems?.edges || []).some(e => ids.includes(e.node?.lineItem?.id)));
        const f = matches.find(x => x.status !== 'CANCELLED') || matches[0] || null;
        return f?.status === 'CANCELLED';
    };
    const validLabelFoIds = new Set((storedLabels || []).map(l => l.fulfillment_order_id));
    const isStaleShippingFO = (fo) => {
        if (fo.status !== 'CLOSED') return false;
        if (fo.deliveryMethod?.methodType === 'PICK_UP') return false;
        if (validLabelFoIds.has(fo.id)) return false;
        const f = getFulfillment(fo);
        if (f && f.status !== 'CANCELLED') return false;
        return true;
    };
    const displayed = order?.cancelledAt
        ? sortedFOs
        : sortedFOs.filter(fo => !isCancelledLabelFO(fo) && !isStaleShippingFO(fo));

    const orderLiById = new Map(lineItems.map(li => [li.id, li]));
    return displayed.filter(fo => {
        const realLiIds = new Set(liIdsOf(fo).filter(id => {
            const li = orderLiById.get(id);
            return li && !isVirtual(li.title);
        }));
        // 2026-10-23 (SURF-106068, Eric): a fee-only FO is not inventory work whatever its delivery method.
        // The Processing Fee split off and auto-fulfilled on its own (method SHIPPING) rendered as a section.
        if (liIdsOf(fo).length > 0 && realLiIds.size === 0) return false;
        if ((fo.deliveryMethod?.methodType || '').toUpperCase() !== 'NONE') return true;
        if (realLiIds.size === 0) return false;          // fee-only FO: not inventory work
        const isDeadFO = (other) => {
            if ((other.status || '').toUpperCase() !== 'CLOSED') return false;
            const ids = (other.fulfillments?.edges || []).map(e => e.node?.id).filter(Boolean);
            return !ids.some(id => {
                const f = fulfillments.find(x => x.id === id);
                return f && (f.status || '').toUpperCase() !== 'CANCELLED';
            });
        };
        const coveredElsewhere = fos.some(other =>
            other.id !== fo.id && !isDeadFO(other)
            && liIdsOf(other).some(id => realLiIds.has(id)));
        return !coveredElsewhere;                         // real orphan items still render
    });
}

function buildFoSubSections(order, storedLabels) {
    const fulfillmentsList = order?.fulfillments || [];
    const out = [];
    for (const fo of selectDisplayFOs(order, storedLabels)) {
        const before = out.length;
        const ownIds = (fo.fulfillments?.edges || []).map(e => e.node?.id).filter(Boolean);
        const ownFulfs = ownIds
            .map(id => fulfillmentsList.find(f => f.id === id))
            .filter(f => f && (f.status || '').toUpperCase() !== 'CANCELLED')
            .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        if (fo.status === 'CLOSED' && !order?.cancelledAt && ownFulfs.length === 0) {
            const dm = (fo.deliveryMethod?.methodType || '').toUpperCase();
            if (dm !== 'PICK_UP') continue;
        }
        for (const f of ownFulfs) {
            const ids = new Set();
            const qtys = {};   // units of each line in THIS fulfillment
            for (const e of (f.fulfillmentLineItems?.edges || [])) {
                const id = e.node?.lineItem?.id;
                if (id) { ids.add(id); qtys[id] = (qtys[id] || 0) + (e.node?.quantity || 0); }
            }
            out.push({ key: `${fo.id}#${f.id}`, fo, fulfillment: f,
                displayMode: 'fulfilled', lineItemIds: ids, qtys,
                sortRank: 1, sortTs: new Date(f.createdAt || 0).getTime() });
        }
        const foIsOpen = fo.status === 'OPEN' || fo.status === 'IN_PROGRESS';
        if (foIsOpen) {
            const ids = new Set();
            const qtys = {};
            for (const e of (fo.lineItems?.edges || [])) {
                if ((e.node?.remainingQuantity || 0) > 0) {
                    const id = e.node?.lineItem?.id;
                    if (id) { ids.add(id); qtys[id] = (qtys[id] || 0) + e.node.remainingQuantity; }
                }
            }
            if (ids.size > 0)
                out.push({ key: `${fo.id}#pending`, fo, fulfillment: null,
                    displayMode: 'pending', pickupStage: foPickupStage(fo),
                    lineItemIds: ids, qtys, sortRank: 0, sortTs: 0 });
        }
        const foEntries = out.slice(before);
        const successEntries = foEntries.filter(e => e.displayMode === 'fulfilled');
        if (successEntries.length > 0) {
            const covered = new Set();
            for (const e of foEntries) e.lineItemIds.forEach(id => covered.add(id));
            const mostRecent = successEntries.slice().sort((a, b) => b.sortTs - a.sortTs)[0];
            for (const e of (fo.lineItems?.edges || [])) {
                const liNode = e.node;
                const liId = liNode?.lineItem?.id;
                if (!liId || covered.has(liId)) continue;
                if ((liNode.remainingQuantity || 0) > 0) continue;
                if ((liNode.totalQuantity || 0) <= 0) continue;
                mostRecent.lineItemIds.add(liId);
                if (mostRecent.qtys) mostRecent.qtys[liId] = liNode.totalQuantity;
            }
        }
        if (out.length === before) {
            const ids = new Set();
            const qtys = {};
            for (const e of (fo.lineItems?.edges || [])) {
                const id = e.node?.lineItem?.id;
                if (id) { ids.add(id); qtys[id] = (qtys[id] || 0) + (e.node?.totalQuantity || 0); }
            }
            out.push({ key: fo.id, fo, fulfillment: null, displayMode: 'legacy',
                lineItemIds: ids, qtys, sortRank: 2, sortTs: 0 });
        }
    }
    out.sort((a, b) => a.sortRank - b.sortRank || a.sortTs - b.sortTs);
    return out;
}

const V3Ctx = createContext({
    fulfillmentShipped: false, splitBundleMap: {}, numberPreviews: {}, shippedLineIds: new Set(), roster: new Map(),
    openPreview: () => {},
});

const FoMenuCtx = createContext(null);

function isSubSectionDone(ss) {
    if (!ss) return false;
    if (ss.displayMode === 'fulfilled') return true;
    return (ss.pickupStage || 0) >= 1;
}

function getFoCarrier(ss) {
    const f = ss?.fulfillment;
    if (!f) return null;
    const ti = (Array.isArray(f.trackingInfo) && f.trackingInfo[0]) || null;
    const number = ti?.number || f.trackingNumber;
    const url = ti?.url || f.trackingUrl;
    const carrier = ti?.company || f.trackingCompany || f.service;
    if (!number && !url && !carrier) return null;
    return { carrier: carrier || '', number: number || '', url: url || '' };
}

function PlayerNumber({ number, override, cls }) {
    if (override) {
        const orig = number || override.order_number || '—';
        return (
            <span className="odv3-ov-stack">
                <span className={`${cls} odv3-ov-orig`} title="Original number from order">#{orig}</span>
                <span className={`${cls} odv3-ov-new`} title={`Team Portal number override${override.tp_updated_at ? ` (updated ${override.tp_updated_at})` : ''}`}>#{override.override_number}</span>
            </span>
        );
    }
    return number ? <span className={cls}>#{number}</span> : null;
}

function PlayerTeam({ team, override }) {
    if (override) {
        const orig = team || override.order_team || '—';
        return (
            <span className="odv3-ov-stack">
                <span className="odv3-ov-orig" title="Original team from order">{orig}</span>
                <span className="odv3-ov-new" title={`Updated in Team Portal${override.tp_updated_at ? ` ${override.tp_updated_at}` : ''}`}>{override.override_team}</span>
            </span>
        );
    }
    return team ? <span>{team}</span> : null;
}

function RqBadge({ entry }) {
    const st = entry?.status;
    if (st === 'required')    return <span className="odv3-rq odv3-rq--req">Required</span>;
    if (st === 'default')     return <span className="odv3-rq odv3-rq--def">Default</span>;
    if (st === 'recommended') return <span className="odv3-rq odv3-rq--rec">Recommended</span>;
    if (st === 'disabled')    return <span className="odv3-rq odv3-rq--off">Off-Spec</span>;
    return null;
}

function VirtualRow({ li, priceOf, fulfilled }) {
    const total = priceOf(li.id);
    return (
        <tr className="odv3-vrow">
            <td/>
            <td>
                <span className="odv3-vtitle">{li.title}</span>
                {fulfilled === true  && <span className="odv3-vchip odv3-vchip--ok">Fulfilled</span>}
                {fulfilled === false && <span className="odv3-vchip">Unfulfilled</span>}
            </td>
            <td><code className="odv3-sku">{li.sku || '—'}</code></td>
            <td/>
            <td className="odv3-n">{li.quantity || 1}</td>
            <td colSpan={3}/>
            <td className="odv3-n odv3-muted">${(total || 0).toFixed(2)}</td>
        </tr>
    );
}

// ─── player / roster helpers ─────────────────────────────────────────────────

function getAttr(li, k) {
    const props = li?.customAttributes || li?.properties || [];
    for (const p of props) {
        const key = (p.name || p.key || '').toLowerCase();
        if (key === k.toLowerCase()) return p.value;
    }
    return null;
}

function parseRoster(order) {
    const raw = getAttr(order, '_soccer_order_info');
    if (!raw) return new Map();
    try {
        const list = JSON.parse(raw);
        const m = new Map();
        for (const r of (Array.isArray(list) ? list : [])) {
            if (r && r.id != null) m.set(String(r.id), r);
        }
        return m;
    } catch { return new Map(); }
}

function getPlayer(li, roster) {
    const playerId = getAttr(li, '_soccer_player_id');
    const entry = (playerId && roster) ? roster.get(String(playerId)) : null;
    const name = getAttr(li, 'Player Name') || getAttr(li, 'Name') || entry?.name || null;
    const number = entry?.config?.number ?? null;
    return {
        playerId, name,
        number: number != null ? String(number) : null,
        gender: entry?.config?.gender ?? null,
        role: entry?.role || null,
        club: entry?.club?.title || null,
        team: entry?.team?.title || null,
    };
}

function groupByPlayer(bundles, roster, tpOv = null) {
    const groups = new Map();
    for (const b of bundles) {
        const p = getPlayer(b.parent, roster);
        const key = p.playerId || p.name || '__unassigned__';
        if (!groups.has(key)) {
            const ov = tpOv ? tpOv.byPlayer(p.playerId, p.name) : { number: null, team: null };
            groups.set(key, { key, name: p.name || 'Unassigned items', number: p.number,
                team: p.team, club: p.club, role: p.role, gender: p.gender, playerId: p.playerId,
                numberOverride: ov.number, teamOverride: ov.team, bundles: [] });
        }
        groups.get(key).bundles.push(b);
    }
    return Array.from(groups.values()).sort((a, b) => {
        if (a.key === '__unassigned__') return 1;
        if (b.key === '__unassigned__') return -1;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
}

function splitBundlesByCategory(bundles, roster) {
    const playerBundles = [];
    const retailBundles = [];
    for (const b of bundles) {
        if (!b.parent || isVirtual(b.parent.title)) continue;
        if (getPlayer(b.parent, roster).name) playerBundles.push(b);
        else retailBundles.push(b);
    }
    return { playerBundles, retailBundles };
}

function sortFoBundles(list, mode, roster) {
    if (!mode || mode === 'order') return list;
    const byTitle = (a, b) =>
        (a.parent?.title || '').localeCompare(b.parent?.title || '', undefined, { sensitivity: 'base' })
        || (a.parent?.sku || '').localeCompare(b.parent?.sku || '');
    const arr = list.slice();
    if (mode === 'product') return arr.sort(byTitle);
    return arr.sort((a, b) => {
        const pa = getPlayer(a.parent, roster).name || '\uffff';
        const pb = getPlayer(b.parent, roster).name || '\uffff';
        return pa.localeCompare(pb, undefined, { sensitivity: 'base' }) || byTitle(a, b);
    });
}

const SHOPIFY_ADMIN_HANDLES = {
    soccerpost: 'soccerpostshop',
    surf:       'surf-soccerpost',
    rush:       'rush-soccerpost',
    citysports: 'citysportsnj',
    u90:        'u90soccer',
    b2b:        'soccerpostb2b',
    stefans:    'stefans-soccer',
};

function writePopupHTML(popup, html) {
    if (!popup || popup.closed) return;
    const doc = popup.document;
    doc.open();
    // eslint-disable-next-line no-restricted-syntax
    doc['write'](html);
    doc.close();
}

// ─── Tools menu ──────────────────────────────────────────────────────────────

function ToolsMenu({ user, onPickup, onToolAction, onResync }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);
    const run = (fn) => () => { setOpen(false); fn(); };
    return (
        <div className="odv3-menu" ref={ref}>
            <button type="button" className="odv3-btn" aria-expanded={open} onClick={() => setOpen(v => !v)}>
                Tools <span className={`odv3-menu-car${open ? ' odv3-menu-car--open' : ''}`}><ChevR/></span>
            </button>
            {open && (
                <div className="odv3-menu-pop" role="menu">
                    {hasRole(user, ['admin', 'site_manager', 'user', 'customer_service', 'franchise']) && (
                        <button type="button" className="odv3-mi" onClick={run(onPickup)}>
                            <span className="odv3-mi-l">Change pickup location</span>
                            <span className="odv3-mi-s">Override the pickup store for this order</span>
                        </button>
                    )}
                    {!allRolesIn(user, ['customer_service', 'store_manager']) && [
                        ['reset_picking',  'Reset picking history', 'Clear all picked quantities'],
                        ['reset_pressing', 'Reset pressing history', 'Clear all pressed quantities'],
                        ['reset_packing',  'Reset packing history',  'Clear all QC pass/fail state'],
                    ].map(([a, l, sub]) => (
                        <button key={a} type="button" className="odv3-mi odv3-mi--danger" onClick={run(() => onToolAction(a))}>
                            <span className="odv3-mi-l">{l}</span>
                            <span className="odv3-mi-s">{sub}</span>
                        </button>
                    ))}
                    {hasRole(user, ['admin']) && (
                        <button type="button" className="odv3-mi" onClick={run(onResync)}>
                            <span className="odv3-mi-l">Force resync from Shopify</span>
                            <span className="odv3-mi-s">Re-fetch FO/fulfillment state if the view looks stale</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── View In menu ────────────────────────────────────────────────────────────

function ViewInMenu({ user, order, orderId, storeKey }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);

    const portalRolesAllowed = !allRolesIn(user, ['crm', 'customer_service', 'finance', 'store_manager', 'external']);
    const adminAllowed = hasRole(user, ['admin', 'site_manager', 'user', 'crm', 'customer_service', 'finance', 'store_manager', 'external']);
    const adminHandle = SHOPIFY_ADMIN_HANDLES[order?._store_key || storeKey] || 'soccerpostshop';
    const returnTo = `/store/${storeKey}/orders/${orderId}?v=3`;
    const close = () => setOpen(false);
    const showOrderEdits = hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service'])
        && ['soccerpost', 'surf', 'rush', 'citysports'].includes(order?._store_key || storeKey)
        && ['team_portal', 'tp_retail', 'tp_addon', 'tp_retail_addon'].includes(order?._cache?.source);

    if (!portalRolesAllowed && !adminAllowed) return null;
    return (
        <div className="odv3-menu" ref={ref}>
            <button type="button" className="odv3-btn" aria-expanded={open} onClick={() => setOpen(v => !v)}>
                Open in <span className={`odv3-menu-car${open ? ' odv3-menu-car--open' : ''}`}><ChevR/></span>
            </button>
            {open && (
                <div className="odv3-menu-pop" role="menu">
                    {portalRolesAllowed && [
                        ['picking-portal', 'Picking Portal', 'Pick items for this order'],
                        ['pressing-portal', 'Pressing Portal', 'Press this order'],
                        ['packing-portal', 'Packing Portal', 'Pack this order'],
                    ].map(([path, label, sub]) => (
                        <Link key={path} to={`/store/${storeKey}/${path}/orders/${orderId}`}
                              state={{ returnTo }} className="odv3-mi" onClick={close}>
                            <span className="odv3-mi-l">{label}</span>
                            <span className="odv3-mi-s">{sub}</span>
                        </Link>
                    ))}
                    {adminAllowed && (
                        <>
                            {portalRolesAllowed && <div className="odv3-mi-div"/>}
                            <a className="odv3-mi" target="_blank" rel="noopener noreferrer" onClick={close}
                               href={`https://admin.shopify.com/store/${adminHandle}/orders/${orderId}`}>
                                <span className="odv3-mi-l">Shopify Admin</span>
                                <span className="odv3-mi-s">Open in Shopify</span>
                            </a>
                        </>
                    )}
                    {showOrderEdits && (
                        <a className="odv3-mi" target="_blank" rel="noopener noreferrer" onClick={close}
                           href={allRolesIn(user, ['franchise'])
                               ? `https://app.soccerpost.com/manager/orders?id=${orderId}`
                               : `https://admin.shopify.com/store/${adminHandle}/apps/soccer-post-production/orders/${orderId}`}>
                            <span className="odv3-mi-l">Order Edits</span>
                            <span className="odv3-mi-s">Edit within Team Portal</span>
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── icon atoms ───────────────────────────────────────────────────────────────

const ChevL = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M12 5l-5 5 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const ChevR = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const PrinterIco = ({ printed }) => {
    const col = printed ? 'var(--v3-printed)' : 'var(--v3-faint)';
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <path d="M6 9V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5"/>
            <path d="M6 14h6"/><path d="M6 18h4"/>
            {printed
                ? <>
                    <circle cx="17" cy="17" r="4" fill="var(--v3-printed)" stroke="none"/>
                    <polyline points="15 17 16.5 18.5 19 15.5" stroke="#fff" strokeWidth="1.5"/>
                  </>
                : <circle cx="17" cy="17" r="3.25" fill="none"
                          strokeDasharray="2 2" strokeWidth="1.5" stroke="var(--v3-faint)"/>
            }
        </svg>
    );
};
// Print-order button icon: plain printer until the order has been printed, then a check badge.
const PrintBtnIco = ({ printed }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
        <path d="M6 9V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5"/>
        <path d="M6 14h6"/><path d="M6 18h4"/>
        {printed && (
            <>
                <circle cx="17" cy="17" r="4.5" fill="currentColor" stroke="none"/>
                <polyline points="15 17 16.5 18.5 19 15.5" stroke="var(--v3-primary)" strokeWidth="1.8"/>
            </>
        )}
    </svg>
);
const PickIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M13 8a3 3 0 1 0-6 0 3 3 0 0 0 6 0Z" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M4 17a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
);
const PressIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="2" y="11" width="16" height="6" rx="2" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M6 11V5a4 4 0 0 1 8 0v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
);
const PackIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M3 7l7-4 7 4v9l-7 4-7-4V7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
        <path d="M10 3v14M3 7l7 4 7-4" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const PersonIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="6" r="3" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M3 18a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
);
const LocIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M10 2a6 6 0 0 0-6 6c0 4 6 10 6 10s6-6 6-10a6 6 0 0 0-6-6Z" stroke="currentColor" strokeWidth="1.6"/>
        <circle cx="10" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
);
const EmailIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const PhoneIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4 2h3l1.5 4-2 1.5a11 11 0 0 0 6 6L14 11.5l4 1.5v3a2 2 0 0 1-2 2C7 18 2 13 2 4a2 2 0 0 1 2-2Z"
              stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const NoteIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M7 7h6M7 11h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
);
const CaretIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const BellIco = () => (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M10 2a6 6 0 0 1 6 6c0 3 1 4 2 5H2c1-1 2-2 2-5a6 6 0 0 1 6-6Z" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M8 15a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
);
const FlagIco = () => (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4 3v14M4 3h9l-2.5 4L13 11H4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const CheckIco = () => (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4 10l5 5 7-8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
// Barcode-in-viewfinder glyph for tracking numbers (Polaris barcode icon).
const TrackIco = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M4.5 6.25a.75.75 0 0 1 .75-.75h.487a.75.75 0 1 0 0-1.5h-.487a2.25 2.25 0 0 0-2.25 2.25v.5a.75.75 0 0 0 1.5 0v-.5Z"/>
        <path d="M4.5 13.76c0 .414.336.75.75.75h.5a.75.75 0 0 1 0 1.5h-.5a2.25 2.25 0 0 1-2.25-2.25v-.5a.75.75 0 0 1 1.5 0v.5Z"/>
        <path d="M14.75 5.5a.75.75 0 0 1 .75.75v.5a.75.75 0 0 0 1.5 0v-.5a2.25 2.25 0 0 0-2.25-2.25h-.5a.75.75 0 0 0 0 1.5h.5Z"/>
        <path d="M15.5 13.76a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 0 0 1.5h.5a2.25 2.25 0 0 0 2.25-2.25v-.5a.75.75 0 0 0-1.5 0v.5Z"/>
        <path d="M6 7.5a.5.5 0 0 1 1 0v5a.5.5 0 0 1-1 0v-5Z"/>
        <path d="M10.5 7a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 1 0v-5a.5.5 0 0 0-.5-.5Z"/>
        <path d="M8 7.625a.625.625 0 1 1 1.25 0v4.75a.625.625 0 1 1-1.25 0v-4.75Z"/>
        <path d="M13 7a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0v-4a1 1 0 0 0-1-1Z"/>
    </svg>
);
const TruckIco = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M2 5h9v8H2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M11 8h4l3 3v2h-7V8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="5.5" cy="14.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="14.5" cy="14.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const TabIcoList = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M2 5h16M2 10h16M2 15h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
);
const TabIcoTimeline = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M6 8h4M6 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="14" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4"/></svg>
);
const TabIcoLog = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 5h12M4 10h8M4 15h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
);

function NotifBanner({ notifications }) {
    const [hidden, setHidden] = useState(() => new Set());
    const list = (Array.isArray(notifications) ? notifications : []).filter((n, i) => !hidden.has(n.id ?? i));
    if (list.length === 0) return null;
    return (
        <>
            {list.map((n, i) => {
                const isOrder = n.scope === 'order' || !!n.order_name;
                const head = isOrder
                    ? `order ${n.order_name || ''}`
                    : (n.team_name ? `${n.club_name || ''} · ${n.team_name}` : (n.club_name || ''));
                return (
                    <div key={n.id ?? i} className="odv3-notif" role="status">
                        <BellIco/>
                        <div className="odv3-notif-body">
                            <div className="odv3-notif-h">Notification — {head}</div>
                            <div className="odv3-notif-m">{n.message}</div>
                        </div>
                        <button type="button" className="odv3-notif-close" aria-label="Dismiss"
                                onClick={() => setHidden(prev => new Set(prev).add(n.id ?? i))}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                        </button>
                    </div>
                );
            })}
        </>
    );
}

// ─── segment bar ─────────────────────────────────────────────────────────────

const MAX_SEGS = 12;
function Segs({ done, total, isDone }) {
    const count = Math.min(total || 1, MAX_SEGS);
    const filled = total > 0 ? Math.round((done / total) * count) : 0;
    return (
        <div className="odv3-segs">
            {Array.from({ length: count }, (_, i) => (
                <div key={i} className={`odv3-seg${(i < filled || isDone) ? ' odv3-seg--on' : ''}`}/>
            ))}
        </div>
    );
}

// ─── dot cells ───────────────────────────────────────────────────────────────

function PieDot({ dots }) {
    const { state, label } = dots?.picked || { state: 'todo', label: '0/0' };
    if (state === 'na') return <span className="odv3-dot odv3-dot--na">—</span>;
    if (state === 'done') {
        return <span className="odv3-dot odv3-dot--done" title={label}><CheckIco/></span>;
    }
    if (state === 'todo') {
        return <span className="odv3-dot odv3-dot--todo" title={label || '—'}/>;
    }
    // partial — geometrically exact arc: angle = n/d × 2π
    const parts = (label || '0/0').split('/').map(Number);
    const n = isFinite(parts[0]) ? parts[0] : 0;
    const d = isFinite(parts[1]) && parts[1] > 0 ? parts[1] : 1;
    const pct = Math.max(0, Math.min(1, n / d));
    const r = 8, cx = 11, cy = 11;
    const angle = pct * 2 * Math.PI;
    const x2 = cx + r * Math.sin(angle);
    const y2 = cy - r * Math.cos(angle);
    const large = angle > Math.PI ? 1 : 0;
    return (
        <svg className="odv3-pie" viewBox="0 0 22 22" title={label}>
            <circle cx={cx} cy={cy} r={r} fill="var(--v3-pie-bg)" stroke="var(--v3-pie-ring)" strokeWidth="2"/>
            <path d={`M${cx} ${cy - r} A${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L${cx} ${cy}Z`}
                  fill="var(--v3-pie-fill)"/>
        </svg>
    );
}

function StDot({ dots, stage }) {
    const { state, label } = dots?.[stage] || { state: 'na', label: '' };
    if (state === 'na') return <span className="odv3-dot odv3-dot--na">—</span>;
    if (state === 'done')    return <span className="odv3-dot odv3-dot--done" title={label}><CheckIco/></span>;
    if (state === 'partial') return <span className="odv3-dot odv3-dot--partial" title={label}/>;
    return <span className="odv3-dot odv3-dot--todo" title={label || '—'}/>;
}

// ─── stages strip ─────────────────────────────────────────────────────────────

function StagesStrip({ pickPressTotals, isOrderPrinted }) {
    const { pickedNum, pickedDen, pressedBundles, pressableBundles, packedNum, packedDen } = pickPressTotals;
    const stages = [
        { icon: <PickIco/>,  label: 'Picked',  done: pickedNum,       total: pickedDen,
          sub: `${pickedDen} item${pickedDen !== 1 ? 's' : ''}` },
        pressableBundles > 0
            ? { icon: <PressIco/>, label: 'Pressed', done: pressedBundles, total: pressableBundles,
                sub: `${pressableBundles} bundle${pressableBundles !== 1 ? 's' : ''}` }
            : null,
        { icon: <PackIco/>,  label: 'Packed',  done: packedNum,       total: packedDen,
          sub: `${packedDen} unit${packedDen !== 1 ? 's' : ''}` },
    ].filter(Boolean);

    return (
        <div className="odv3-stages" role="region" aria-label="Production stages">
            <button type="button" className="odv3-pstage"
                    aria-pressed={isOrderPrinted ? 'true' : 'false'}
                    title={isOrderPrinted ? 'Printed' : 'Not yet printed'}>
                <span className="odv3-pst-l">{isOrderPrinted ? 'Printed' : 'Not printed'}</span>
                <PrinterIco printed={isOrderPrinted}/>
            </button>
            {stages.map(({ icon, label, done, total, sub }) => {
                const isDone = total > 0 && done >= total;
                return (
                    <div key={label} className={`odv3-stage${isDone ? ' odv3-stage--done' : ''}`}>
                        <div className="odv3-stage-top">
                            {icon}
                            <span>{label}</span>
                            <span className="odv3-stage-val">
                                {done}<small>/{total} {sub.split(' ').slice(1).join(' ')}</small>
                            </span>
                        </div>
                        <Segs done={done} total={total} isDone={isDone}/>
                    </div>
                );
            })}
        </div>
    );
}

function StageCell({ stage, dots, modify, groupKey, qty, title, liId, canClick, cascade }) {
    const active = !!(modify?.on && canClick && groupKey);
    const pend = active && modify.pending.has(`${groupKey}::${stage}`);
    const cur = dots?.[stage]?.state;
    if (!active) {
        return stage === 'picked' ? <PieDot dots={dots}/> : <StDot dots={dots} stage={stage}/>;
    }
    const entry = modify.pending.get(`${groupKey}::${stage}`);
    const eff = entry ? (entry.desired ? 'done' : 'todo') : cur;
    const state = eff === 'done' ? 'done' : eff === 'partial' ? 'partial' : 'todo';
    return (
        <button type="button" role="checkbox"
                aria-checked={state === 'done' ? 'true' : state === 'partial' ? 'mixed' : 'false'}
                className={`odv3-cb odv3-cb--${state}${pend ? ' odv3-cb--pend' : ''}`}
                title={pend ? 'Pending change — click to undo' : 'Click to stage a change'}
                onClick={() => modify.toggle({
                    groupKey, stage, qty, title, currentState: cur, cascade,
                    locId: modify.locIdByLiId?.[liId] || '',
                })}>
            {state === 'done' && <CheckIco/>}
            {state === 'partial' && <span className="odv3-cb-dash"/>}
        </button>
    );
}

// ─── bundle row ───────────────────────────────────────────────────────────────

function BundleRow({ b, open, onToggle, portalState, lineFlags, priceOf, tpReqByLiId, modify }) {
    const { fulfillmentShipped, splitBundleMap, numberPreviews: npCtx, shippedLineIds, roster, openPreview } = useContext(V3Ctx);
    const { parent, children } = b;
    if (!parent) return null;
    if (isVirtual(parent.title)) {
        return <VirtualRow li={parent} priceOf={priceOf} fulfilled={shippedLineIds?.has(parent.id) ?? null}/>;
    }
    const refunded = !!b.refunded;
    const kids = (children || []).filter(c => !isVirtual(c?.title));

    const dots = refunded
        ? { picked: { state: 'na', label: '' }, pressed: { state: 'na', label: '' }, packed: { state: 'na', label: '' } }
        : dotsForBundle(parent, children || [], portalState, fulfillmentShipped, lineFlags);

    const { front, back } = (() => {
        // A component split from its bundle shows its own product image, not the bundle's front/back preview.
        if (b.splitFrom) return { front: null, back: null };
        const groupAttrs = b.groupData?.customAttributes || parent.lineItemGroup?.customAttributes || [];
        const childAttrs = (children || []).flatMap(c => c?.customAttributes || []);
        return getPreviewImageUrls([...groupAttrs, ...(parent.customAttributes || []), ...childAttrs]);
    })();
    const fallbackImg = parent.image?.url || parent.product?.featuredImage?.url || null;
    const imgs = front || back ? [front, back].filter(Boolean) : (fallbackImg ? [fallbackImg] : []);
    const single = imgs.length < 2;
    const jumble = splitBundleMap[parent.lineItemGroup?.id] ?? splitBundleMap[parent.id];
    const returnable = String(getAttr(parent, 'Returnable') || '').toLowerCase();

    const title = parent.title || '—';
    const sku = parent.sku || '—';
    const variant = (parent.variant?.title && parent.variant.title !== 'Default Title')
        ? parent.variant.title : (parent.variantTitle || '');
    const price = priceOf(parent.id) + (children || []).reduce((sum, c) => sum + (priceOf(c.id) || 0), 0);
    const pl = getPlayer(parent, roster);
    // Name / Number chips follow what is actually printed as part of the bundle (V1 rules):
    // a name or number sub-product, or a Name / Number attribute on the parent or a child.
    const bundleAttr = (...keys) => {
        for (const li of [parent, ...kids]) {
            for (const k of keys) {
                const v = getAttr(li, k);
                if (v != null && String(v).trim() !== '' && !String(k).startsWith('_')) return String(v).trim();
            }
        }
        return '';
    };
    const nameChild = kids.find(c => /\bname\b/i.test(c.title || ''));
    const numberChild = kids.find(c => /\bnumber\b/i.test(c.title || ''));
    const printedName = (nameChild || bundleAttr('Name')) ? (bundleAttr('Name') || pl.name || '') : '';
    const printedNumber = (() => {
        const attr = bundleAttr('Number', '#');
        if (attr) return attr;
        if (!numberChild) return '';
        const fromChild = getPrintedNumberAttr(numberChild.customAttributes)
            || getPrintedNumberAttr(parent.customAttributes)
            || getPreviewFor(numberChild, npCtx)?.defaultValue
            || pl.number || '';
        return String(fromChild || '');
    })();
    const hasNameChip = !!(nameChild || bundleAttr('Name'));
    const hasNumberChip = !!(numberChild || bundleAttr('Number', '#'));
    const tpReq = tpReqByLiId?.[parent.id] || null;
    const hasDeco = (children || []).some(c => isDecoration(c));

    const thumb = (src, alt, cls) => (
        <div className={`odv3-thumb ${cls}`}>
            {src
                ? <SmartProductImage src={src} alt={alt} size={120}
                    style={{ width: '100%', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block' }}/>
                : <img src={MISSING_IMAGE_SVG} alt="" style={{ width: '60%', opacity: .4 }}/>}
        </div>
    );
    // Preview popup: front/back art, SKU · variant · price, then what the bundle is made of.
    const showPreview = () => {
        const comps = [];
        // V1-style pick badges: "n/n Picked" (green), "x/y Picked" (orange), UNPICKED (red, only once
        // anything on the order has been picked).
        const anyPicking = Object.values(portalState || {}).some(p => (p?.qty_picked || 0) > 0)
            || Object.values(lineFlags || {}).some(f => f?.picked);
        // A per-line server flag (lineFlags) wins, as in V1: flag.picked === false => UNPICKED even when
        // nothing on the order has been picked yet.
        const pickBadge = (d, li) => {
            if (refunded || !d || d.state === 'na') return null;
            const flag = lineFlags?.[li?.id];
            if (flag && flag.picked !== true) return { cls: 'unpicked', text: 'UNPICKED' };
            if (d.state === 'done') return { cls: 'full', text: `${d.label} Picked` };
            if (d.state === 'partial') return { cls: 'partial', text: `${d.label} Picked` };
            return (flag || anyPicking) ? { cls: 'unpicked', text: 'UNPICKED' } : null;
        };
        if (kids.length > 0) {
            comps.push({ title: `Blank garment — ${title}`, sku, img: fallbackImg || front, badge: pickBadge(blankDots.picked, parent) });
        }
        kids.forEach(c => {
            const cdots = dotsForRow(isDecoration(c) ? 'decoration' : 'standalone', c,
                psForSku(portalState, c.sku), fulfillmentShipped, lineFlags?.[c.id]);
            const cPreview = getPreviewFor(c, npCtx);
            const cNum = getPrintedNumberAttr(c.customAttributes)
                || getPrintedNumberAttr(parent?.customAttributes)
                || cPreview?.defaultValue || '';
            comps.push({
                title: c.title || '—', sku: c.sku || '',
                img: c.image?.url || c.product?.featuredImage?.url || null,
                preview: cPreview && cNum ? { preview: cPreview, number: cNum } : null,
                // Printed value for name / number sub-products (same rules as the item-row chips).
                // (every name / number sub-product gets its own chip — a bundle can carry two of each)
                chip: /\bnumber\b/i.test(c.title || '')
                        ? ((getPrintedNumberAttr(c.customAttributes) || printedNumber) ? `#${getPrintedNumberAttr(c.customAttributes) || printedNumber}` : '')
                    : /\bname\b/i.test(c.title || '')
                        ? (getAttr(c, 'Name') || printedName || '') : '',
                badge: pickBadge(cdots.picked, c),
            });
        });
        openPreview({
            title, sku: parent.sku || '', variant, price: price > 0 ? `$${price.toFixed(2)}` : '',
            imgs: [front, back].filter(Boolean).length === 2
                ? [{ src: front, label: 'Front' }, { src: back, label: 'Back' }]
                : imgs.map(src => ({ src, label: '' })),
            comps,
            pressed: !refunded && dots.pressed?.state === 'done',
            // A plain item (no components) has no component list to carry its pick chip.
            badge: kids.length === 0 ? pickBadge(blankDots.picked, parent) : null,
        });
    };
    const blankDots = refunded ? dots : dotsForRow(
        'standalone', parent, psForSku(portalState, parent.sku), fulfillmentShipped, lineFlags?.[parent.id]);

    return (
        <>
            <tr className={`odv3-row${open ? ' odv3-row--open' : ''}`}>
                <td>
                    {kids.length > 0 && !refunded && (
                        <button className="odv3-caret" onClick={onToggle}
                                aria-expanded={open} aria-label={open ? 'Collapse' : 'Expand'}>
                            <CaretIco/>
                        </button>
                    )}
                </td>
                <td>
                    <div className="odv3-item">
                        <button type="button" className="odv3-thumbs" onClick={showPreview}
                                aria-label={`Preview ${title}`} title="View preview and components">
                            {imgs.length > 0
                                ? imgs.map((src, i) => <React.Fragment key={i}>{thumb(src, title, single ? 'odv3-thumb--one' : '')}</React.Fragment>)
                                : thumb(null, title, 'odv3-thumb--one')}
                        </button>
                        <div className="odv3-item-text">
                            <div className="odv3-it-t">
                                {title}
                                {jumble != null && <JumbleBadge color={jumble}/>}
                                {kids.length > 0 && <span className="odv3-plus">+{kids.length}</span>}
                            </div>
                            {b.splitFrom && (
                                <div className="odv3-it-s odv3-splitnote">
                                    Part of {b.splitFrom.title || 'a bundle'} · shipped separately from the rest of the bundle
                                </div>
                            )}
                            {(tpReq?.status || returnable === 'no' || returnable.includes('exchange')) && (
                                <div className="odv3-it-s">
                                    <RqBadge entry={tpReq}/>
                                    {returnable === 'no' && <span className="odv3-fs">Final sale</span>}
                                    {returnable.includes('exchange') && <span className="odv3-fs odv3-fs--ex">Exchange only</span>}
                                </div>
                            )}
                            {(pl.name || hasNameChip || hasNumberChip) && (
                                <div className="odv3-it-s">
                                    {pl.name && <span className="odv3-pl">{pl.name}</span>}
                                    {hasNameChip && <span className="odv3-rnum" title="Name printed on this item">{printedName || 'Name'}</span>}
                                    {hasNumberChip && <span className="odv3-rnum" title="Number printed on this item">{printedNumber ? `#${printedNumber}` : '#'}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td><code className="odv3-sku">{sku}</code><SkuNotificationCell sku={parent.sku}/></td>
                <td style={{ color: 'var(--v3-text-2)' }}>{variant}</td>
                <td className="odv3-n">{parent.quantity || 1}</td>
                {['picked', 'pressed', 'packed'].map(st => (
                    <td key={st} className="odv3-c">
                        <StageCell stage={st} dots={dots} modify={modify}
                                   groupKey={parent.sku} qty={parent.quantity || 1} title={title}
                                   liId={parent.id}
                                   cascade={st === 'picked' && hasDeco ? { pressedState: dots.pressed.state } : null}
                                   canClick={!refunded && (st === 'picked' || (st === 'pressed' && hasDeco))}/>
                    </td>
                ))}
                <td className="odv3-n">{price > 0 ? `$${price.toFixed(2)}` : '—'}</td>
            </tr>
            {open && !refunded && kids.length > 0 && (
                <tr className="odv3-comp">
                    <td/>
                    <td>
                        <div className="odv3-item">
                            {thumb(fallbackImg || front, title, 'odv3-thumb--sm')}
                            <div className="odv3-item-text">
                                <div className="odv3-it-t2">{title}</div>
                                <div className="odv3-it-s">Core garment · picked, then pressed</div>
                            </div>
                        </div>
                    </td>
                    <td><code className="odv3-sku">{sku}</code></td>
                    <td className="odv3-faint">{variant || '—'}</td>
                    <td className="odv3-n">{parent.quantity || 1}</td>
                    <td className="odv3-c"><StDot dots={blankDots} stage="picked"/></td>
                    <td className="odv3-c"><StDot dots={dots} stage="pressed"/></td>
                    <td className="odv3-c"><StDot dots={dots} stage="packed"/></td>
                    <td className="odv3-n odv3-muted">$0.00</td>
                </tr>
            )}
            {open && !refunded && kids.map((c, i) => {
                const cps = psForSku(portalState, c.sku);
                const cdots = dotsForRow(
                    isDecoration(c) ? 'decoration' : 'standalone',
                    c, cps, fulfillmentShipped, lineFlags?.[c.id]
                );
                const cPreview = getPreviewFor(c, npCtx);
                const cNum = getPrintedNumberAttr(c.customAttributes)
                    || getPrintedNumberAttr(parent?.customAttributes)
                    || cPreview?.defaultValue || '';
                const cJumble = splitBundleMap[c.lineItemGroup?.id] ?? splitBundleMap[c.id];
                const cImg = c.image?.url || c.product?.featuredImage?.url;
                const cTitle = c.title || '—';
                const personalization = /player\s*(name|number)/i.test(cTitle);
                const isLast = i === kids.length - 1;
                const cPrice = priceOf(c.id) || 0;
                return (
                    <tr key={c.id || i} className={`odv3-comp${isLast ? ' odv3-comp--last' : ''}`}>
                        <td/>
                        <td>
                            <div className="odv3-item">
                                {cPreview && cNum
                                    ? <div className="odv3-thumb odv3-thumb--sm"><NumberPreviewThumb preview={cPreview} number={cNum} size={36} borderColor="#e5e7eb"/></div>
                                    : thumb(cImg, cTitle, 'odv3-thumb--sm')}
                                <div className="odv3-item-text">
                                    <div className="odv3-it-t2">
                                        {cTitle}
                                        {cJumble != null && <JumbleBadge color={cJumble}/>}
                                    </div>
                                    <div className="odv3-it-s">
                                        {personalization
                                            ? 'Personalization · pressed on back'
                                            : isDecoration(c) ? 'Embellishment · heat-pressed on garment' : 'Component'}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td><code className="odv3-sku">{c.sku || '—'}</code><SkuNotificationCell sku={c.sku}/></td>
                        <td className="odv3-faint">—</td>
                        <td className="odv3-n">{c.quantity || 1}</td>
                        <td className="odv3-c">
                            <StageCell stage="picked" dots={cdots} modify={modify}
                                       groupKey={c.sku} qty={c.quantity || 1} title={cTitle}
                                       liId={parent.id} canClick={isDecoration(c) && !!c.sku}/>
                        </td>
                        <td className="odv3-c"><span className="odv3-dot odv3-dot--na">—</span></td>
                        <td className="odv3-c"><span className="odv3-dot odv3-dot--na">—</span></td>
                        <td className="odv3-n odv3-muted">${cPrice.toFixed(2)}</td>
                    </tr>
                );
            })}
        </>
    );
}

function HeadCheck({ modify, targets, stage, label }) {
    if (!modify?.on || !targets[stage].length) return label;
    const state = modify.stageState(targets[stage], stage);
    return (
        <span className="odv3-hcheck">
            <button type="button" role="checkbox"
                    aria-checked={state === 'done' ? 'true' : state === 'partial' ? 'mixed' : 'false'}
                    className={`odv3-cb odv3-cb--${state}`}
                    title={state === 'done' ? `Untick all ${label.toLowerCase()}` : `Tick all ${label.toLowerCase()}`}
                    onClick={() => modify.selectStage(stage, targets[stage])}>
                {state === 'done' && <CheckIco/>}
                {state === 'partial' && <span className="odv3-cb-dash"/>}
            </button>
            {label}
        </span>
    );
}

function ItemsTable({ bundles, groups = null, collapsedGroups = null, onToggleGroup = null, groupScope = '', portalState, lineFlags, priceOf, tpReqByLiId, openBundles, onToggleBundle, modify }) {
    if (groups) bundles = groups.flatMap(g => g.bundles);
    const rowOf = (b) => (
        <BundleRow
            key={b.parent?.id || b.key}
            b={b}
            open={openBundles.has(b.parent?.id)}
            onToggle={() => onToggleBundle(b.parent?.id)}
            portalState={portalState}
            lineFlags={lineFlags}
            priceOf={priceOf}
            tpReqByLiId={tpReqByLiId}
            modify={modify}
        />
    );
    const targets = modify?.on ? modify.targetsFor(bundles) : { picked: [], pressed: [] };
    return (
        <div className="odv3-tscroll">
            <table className="odv3-tbl">
                <thead><tr>
                    <th style={{ width: 34 }}/>
                    <th>Item</th>
                    <th>SKU</th>
                    <th>Variant</th>
                    <th className="odv3-n">Qty</th>
                    <th className="odv3-c"><HeadCheck modify={modify} targets={targets} stage="picked" label="Pick"/></th>
                    <th className="odv3-c"><HeadCheck modify={modify} targets={targets} stage="pressed" label="Press"/></th>
                    <th className="odv3-c">Pack</th>
                    <th className="odv3-n">Price</th>
                </tr></thead>
                <tbody>
                    {groups
                        ? groups.map(g => (
                            <React.Fragment key={g.key}>
                                {g.heading && (
                                    <tr className="odv3-grp" data-grp={g.key} onClick={() => onToggleGroup?.(`${groupScope}${g.key}`)}>
                                        <td colSpan={9}>
                                            <span className={`odv3-grp-caret${collapsedGroups?.has(`${groupScope}${g.key}`) ? '' : ' odv3-grp-caret--open'}`}>▶</span>
                                            {g.heading}
                                        </td>
                                    </tr>
                                )}
                                {!(g.heading && collapsedGroups?.has(`${groupScope}${g.key}`)) && g.bundles.map(rowOf)}
                            </React.Fragment>
                        ))
                        : bundles.map(rowOf)}
                </tbody>
            </table>
        </div>
    );
}

const tpFmt = (v) => (v ? String(v).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '');
const qtyOfLi = (li) => parseInt(li?.currentQuantity ?? li?.quantity ?? 0, 10) || 0;
const sumItems = (bs) => bs.reduce((sum, b) =>
    sum + qtyOfLi(b.parent) + (b.children || []).reduce((cs, c) => cs + qtyOfLi(c), 0), 0);
const countPressable = (bs) => bs.filter(b => (b.children || []).length > 0).length;

// Player groups (+ a Retail items group) with the Team Portal role / specialization and the roster gender.
function buildPlayerSections(bundles, roster, tpOv, tpPlayers) {
    const { playerBundles, retailBundles } = splitBundlesByCategory(bundles, roster);
    return [
        ...groupByPlayer(playerBundles, roster, tpOv).map(g => {
            const tp = tpPlayers?.get(String(g.playerId));
            return { ...g, title: g.name, gender: tpFmt(g.gender), role: tpFmt(tp?.role || g.role), specialization: tpFmt(tp?.specialization) };
        }),
        ...(retailBundles.length ? [{ key: '__retail__', title: 'Retail items', retail: true, bundles: retailBundles }] : []),
    ];
}

function PlayerCards({ sections, activeKey = null, onPick }) {
    const initials = (nm) => (nm || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
        <div className="odv3-roster">
            {sections.map(sec => (
                <button key={sec.key} type="button"
                        className={`odv3-rcard${activeKey === sec.key ? ' odv3-rcard--on' : ''}`}
                        onClick={() => onPick(sec.key)}>
                    <span className="odv3-rav">{sec.retail ? 'R' : initials(sec.title)}</span>
                    <span className="odv3-rcard-txt">
                        <span className="odv3-rcard-name">
                            {sec.title}
                            {!sec.retail && <PlayerNumber number={sec.number} override={sec.numberOverride} cls="odv3-rnum"/>}
                        </span>
                        {(sec.gender || sec.role || sec.specialization) && (
                            <span className="odv3-rcard-role">{[sec.gender, sec.role, sec.specialization].filter(Boolean).join(' · ')}</span>
                        )}
                        <span className="odv3-rcard-sub">
                            {[(sec.team || sec.teamOverride) ? <PlayerTeam key="t" team={sec.team} override={sec.teamOverride}/> : null,
                              `${countPressable(sec.bundles)} bundles`, `${sumItems(sec.bundles)} items`]
                                .filter(Boolean)
                                .map((x, i) => <React.Fragment key={i}>{i > 0 ? ' · ' : ''}{x}</React.Fragment>)}
                        </span>
                    </span>
                </button>
            ))}
        </div>
    );
}

// Sub heading above a player's items: name + number, club, team.
function PlayerHeading({ sec }) {
    return sec.retail
        ? <span className="odv3-grp-name">Other items</span>
        : (
            <>
                <span className="odv3-grp-name">
                    {sec.title || sec.name}
                    <PlayerNumber number={sec.number} override={sec.numberOverride} cls="odv3-rnum"/>
                </span>
                {sec.club && <span className="odv3-grp-meta">{sec.club}</span>}
                {(sec.team || sec.teamOverride) && <span className="odv3-grp-meta"><PlayerTeam team={sec.team} override={sec.teamOverride}/></span>}
            </>
        );
}

// Scroll to the first sub heading for a player, clear of the sticky order header, and flash it.
function scrollToPlayerHeading(key) {
    const row = [...document.querySelectorAll('.odv3-grp')].find(r => r.dataset.grp === key);
    if (!row) return;
    const head = document.querySelector('.odv3-head');
    row.style.scrollMarginTop = `${(head?.offsetHeight || 0) + 8}px`;
    row.scrollIntoView({ behavior: 'smooth', block: 'start' });
    row.classList.remove('odv3-grp--flash');
    void row.offsetWidth;
    row.classList.add('odv3-grp--flash');
}

function PlayersTab({ bundles, roster, hasTpTag, tpOv, tpPlayers, portalState, lineFlags, priceOf, tpReqByLiId, openBundles, onToggleBundle, modify }) {
    const sections = useMemo(() => buildPlayerSections(bundles, roster, tpOv, tpPlayers), [bundles, roster, tpOv, tpPlayers]);
    const [activeKey, setActiveKey] = useState(null);
    if (sections.length === 0) return <div className="odv3-stub">No {hasTpTag ? 'players' : 'items'} on this order</div>;
    const sel = sections.find(sec => sec.key === activeKey) || sections[0];
    return (
        <div className="odv3-players">
            <div className="odv3-players-hint">Select a {hasTpTag ? 'player' : 'group'} to view their items</div>
            <PlayerCards sections={sections} activeKey={sel.key} onPick={setActiveKey}/>
            <ItemsTable bundles={sel.bundles} portalState={portalState} lineFlags={lineFlags}
                priceOf={priceOf} tpReqByLiId={tpReqByLiId}
                openBundles={openBundles} onToggleBundle={onToggleBundle} modify={modify}/>
        </div>
    );
}

// ─── fulfillment section ──────────────────────────────────────────────────────

function FoSection({ subSection, bundles, roster, groupBy, tpOv, collapsedGroups, onToggleGroup, portalState, lineFlags, priceOf, tpReqByLiId, openBundles, onToggleBundle, modify, order, onModify }) {
    const menuCtx = useContext(FoMenuCtx);
    const { user } = useAuth();
    const { splitBundleMap } = useContext(V3Ctx);
    const { fo, fulfillment, displayMode } = subSection;
    const foIds = subSection.lineItemIds;
    // A line split across fulfillments (22 hats shipped 7 + 15) shows only this section's units.
    const secQtys = subSection.qtys || {};
    const inSec = (li) => {
        const q = li && secQtys[li.id];
        return q != null && q > 0 && q < (parseInt(li.quantity || 0, 10) || 0) ? { ...li, quantity: q, currentQuantity: q } : li;
    };
    bundles = bundles.map(b => ({ ...b, parent: inSec(b.parent), children: (b.children || []).map(inSec) }));
    // Jumbled bundle: a component that ships in THIS fulfillment while the bundle's garment is in another one
    // gets its own row here (badge + "Part of …"), otherwise it only shows nested under the garment elsewhere.
    const splitOut = [];
    bundles.forEach(b => {
        if (!b.parent || b.refunded || foIds.has(b.parent.id)) return;
        (b.children || []).forEach(c => {
            if (!foIds.has(c.id) || isVirtual(c.title) || (c.quantity ?? 0) <= 0) return;
            if ((splitBundleMap[c.lineItemGroup?.id] ?? splitBundleMap[c.id]) == null) return;
            splitOut.push({
                ...b, key: `split-${b.parent.id}-${c.id}`, children: [], splitFrom: b.parent,
                parent: { ...c, customAttributes: [...(c.customAttributes || []),
                    ...(b.parent.customAttributes || []).filter(a => (a.key || a.name) !== '_soccer_key')] },
            });
        });
    });
    const foBundles = sortFoBundles([...bundles.filter(b => {
        if (!b.parent || !foIds.has(b.parent.id)) return false;
        return (b.parent.quantity ?? 0) > 0 || (b.children || []).some(c => (c.quantity ?? 0) > 0);
    }), ...splitOut], groupBy, roster);
    if (foBundles.length === 0 && foIds.size === 0) return null;

    // Player view: every player's items under their own sub heading (name + number, club, team).
    let playerGroups = null;
    if (groupBy === 'player') {
        const named = foBundles.filter(b => b.parent && !isVirtual(b.parent.title) && getPlayer(b.parent, roster).name);
        const rest = foBundles.filter(b => !named.includes(b));
        const gs = [
            ...groupByPlayer(named, roster, tpOv).map(g => ({ key: g.key, bundles: g.bundles, heading: <PlayerHeading sec={g}/> })),
            ...(rest.length ? [{ key: '__retail__', bundles: rest, heading: <PlayerHeading sec={{ retail: true }}/> }] : []),
        ];
        if (gs.length > 1) playerGroups = gs;
    }

    const locationName = fo.assignedLocation?.name || fo.assignedLocation?.location?.name
        || fo.assignedLocation?.address?.city || '—';
    const carrier = getFoCarrier(subSection);
    const statusLabel = formatFoStatus(subSection);
    const done = isSubSectionDone(subSection);
    const statusCls = done ? 'ful' : (fo.status === 'IN_PROGRESS' ? 'par' : 'unf');

    const isOpen = displayMode === 'fulfilled' ? false : (fo.status === 'OPEN' || fo.status === 'IN_PROGRESS');
    const pickup = isPickupMethod(fo.deliveryMethod?.methodType);
    const isShipping = (fo.deliveryMethod?.methodType || '').toUpperCase() === 'SHIPPING';
    const pickupLocationName = (menuCtx?.storePickupOverride || '').trim() || fo.deliveryMethod?.presentedName || '';
    const atPickupLocation = pickup && !!pickupLocationName && (fo.assignedLocation?.name || '') === pickupLocationName;
    const isCancelledLabel = !isOpen && fulfillment?.status === 'CANCELLED' && fo.requestStatus === 'UNSUBMITTED';
    const isTrulyFulfilled = !isOpen && !isCancelledLabel;
    const storedLabel = (menuCtx?.storedLabels || []).find(l => {
        if (l.fulfillment_order_id !== fo.id) return false;
        if (displayMode === 'pending') return false;
        if (displayMode === 'fulfilled' && l.fulfillment_id) return l.fulfillment_id === fulfillment?.id;
        return true;
    }) || null;
    const hasPhysical = bundles.some(b => [b.parent, ...(b.children || [])]
        .some(li => li && foIds.has(li.id) && !isVirtual(li.title)));
    const canReprint = !!(menuCtx && fo.id
        && ['admin', 'site_manager', 'user', 'franchise', 'store_manager'].includes(user?.role)
        && (storedLabel || isTrulyFulfilled || atPickupLocation || fo._pickupState?.transferred_at)
        && hasPhysical);
    const openPrint = (kind) => {
        const sk = menuCtx?.storeKey || 'soccerpost';
        let url;
        if (kind === 'label' && storedLabel) {
            const tail = (storedLabel.shopify_label_gid || '').split('/').pop();
            url = `/api/shipping-labels/${tail}/combined-html?store=${encodeURIComponent(sk)}`;
        } else {
            const qs = new URLSearchParams({ fulfillment_order_id: fo.id, skip_transfer: 'true', store: sk });
            url = `/api/orders/${menuCtx.orderId}/packing-slip-print?${qs}`;
        }
        const standalone = (typeof window !== 'undefined') && (
            window.navigator?.standalone === true
            || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
        );
        if (standalone) {
            window.location.href = `${url}${url.includes('?') ? '&' : '?'}return=${encodeURIComponent(window.location.href)}`;
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const bulk = (order?.bulk_shipment_labels || [])
        .filter(l => String(l.fulfillment_order_id) === String((fo.id || '').split('/').pop()));
    const canModify = !allRolesIn(user, ['crm', 'finance', 'customer_service', 'store_manager', 'external']);

    return (
        <div className="odv3-fo" id={`odv3-fo-${subSection.key}`}>
            <div className="odv3-fo-head">
                <span className="odv3-fo-loc"><LocIco/>{locationName}</span>
                <span className={`odv3-badge odv3-badge--sm odv3-badge--${statusCls}`}>
                    <span className="odv3-badge-d"/>
                    {statusLabel}
                </span>
                {fulfillment?.name && <span className="odv3-fo-tr" title="Shopify fulfillment number">{fulfillment.name}</span>}
                {bulk.map(l => (l.tracking_url
                    ? <a key={l.id} className="odv3-fo-tr" href={l.tracking_url} target="_blank" rel="noreferrer"
                         title={`Bulk shipment scanned ${new Date(l.scanned_at).toLocaleString()}`}><TruckIco/>{l.tracking_number}</a>
                    : <span key={l.id} className="odv3-fo-tr"
                            title={`Hand-carried box, scanned ${new Date(l.scanned_at).toLocaleString()}`}><TruckIco/>{l.tracking_number}</span>))}
                {(carrier?.carrier || (isShipping && fo.deliveryMethod?.presentedName)) && (
                    <span className="odv3-fo-tr"><TruckIco/>{carrier?.carrier || fo.deliveryMethod.presentedName}</span>
                )}
                {isOpen && isShipping && !carrier?.number && !carrier?.url && !bulk.length && (
                    <span className="odv3-fo-tr"><TrackIco/>No tracking yet</span>
                )}
                {carrier && (carrier.number || carrier.url) && (carrier.url
                    ? <a className="odv3-fo-track odv3-fo-tr" href={carrier.url} target="_blank" rel="noreferrer"><TrackIco/>{carrier.number || 'Tracking'}</a>
                    : <span className="odv3-fo-track odv3-fo-tr"><TrackIco/>{carrier.number}</span>)}
                <span className="odv3-fo-sp"/>
                {isOpen && canModify && !modify?.on && (
                    <button type="button" className="odv3-btn odv3-btn--slim" onClick={onModify}>Mod. pick/press</button>
                )}
                {isOpen && menuCtx && fo.id && (
                    <FulfillmentMenu
                        triggerClass="odv3-btn odv3-btn--slim"
                        fo={fo}
                        fulfillment={fulfillment}
                        orderId={menuCtx.orderId}
                        isOpen={isOpen}
                        isShipping={isShipping}
                        hasLabel={!!storedLabel}
                        pickup={pickup}
                        atPickupLocation={atPickupLocation}
                        pickupReadyAt={fo._pickupState?.ready_for_pickup_at || null}
                        pickedUpAt={fo._pickupState?.picked_up_at || null}
                        isCancelledLabel={isCancelledLabel}
                        isTrulyFulfilled={isTrulyFulfilled}
                        storeKey={menuCtx.storeKey}
                        storeFetch={menuCtx.storeFetch}
                        siblingFos={menuCtx.siblings.filter(sib => sib.id !== fo.id)}
                        storePickupOverride={menuCtx.storePickupOverride}
                        onAfterAction={menuCtx.onAfterAction}
                        onToast={menuCtx.onToast}
                    />
                )}
                {!isOpen && canReprint && (
                    <>
                        {storedLabel && (
                            <button type="button" className="odv3-btn odv3-btn--slim" onClick={() => openPrint('label')}>Reprint label</button>
                        )}
                        <button type="button" className="odv3-btn odv3-btn--slim" onClick={() => openPrint('slip')}>Packing slip</button>
                    </>
                )}
            </div>
            <ItemsTable modify={modify} bundles={foBundles} groups={playerGroups}
                collapsedGroups={collapsedGroups} onToggleGroup={onToggleGroup} groupScope={`${subSection.key}|`}
                portalState={portalState} lineFlags={lineFlags}
                priceOf={priceOf} tpReqByLiId={tpReqByLiId}
                openBundles={openBundles} onToggleBundle={onToggleBundle}/>
        </div>
    );
}

const FIN_TONE = {
    PAID: 'ful', PARTIALLY_REFUNDED: 'unf', REFUNDED: 'can', PARTIALLY_PAID: 'unf', PENDING: 'unf', VOIDED: 'neu',
};

// Same lines as the V1 summary: financial status, subtotal (gross, before discounts), discounts,
// pickup/shipping, each tax line with its rate, total, paid, each refund with its note, and Net.
function OrderSummary({ order, lineItems, counts }) {
    const num = (set) => { const n = parseFloat(set?.shopMoney?.amount ?? set?.amount ?? 0); return isFinite(n) ? n : 0; };
    const fmtNum = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
    const fmt = (set) => fmtNum(num(set));
    const subtotalGross = (lineItems || []).reduce((sum, li) => {
        const ot = parseFloat(li.originalTotalSet?.shopMoney?.amount);
        if (isFinite(ot)) return sum + ot;
        const up = parseFloat(li.originalUnitPriceSet?.shopMoney?.amount) || 0;
        return sum + up * (parseInt(li.quantity || 0, 10) || 0);
    }, 0);
    const discount = num(order.currentTotalDiscountsSet);
    const tax = num(order.totalTaxSet);
    const refunded = num(order.totalRefundedSet);
    const received = num(order.totalReceivedSet);
    const total = num(order.totalPriceSet);
    const taxLines = order.taxLines || [];
    const discountApps = (order.discountApplications?.edges || []).map(e => e.node);
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    const firstPickupFO = fos.find(f => (f.deliveryMethod?.methodType || '').toUpperCase() === 'PICK_UP');
    const overrideLoc = (order.store_pickup_override || '').trim() || null;
    const isPickup = !!firstPickupFO || !!overrideLoc;
    const pickupLocationName = overrideLoc
        || firstPickupFO?.deliveryMethod?.presentedName
        || firstPickupFO?.assignedLocation?.name
        || '';
    const fs = order.displayFinancialStatus || '';
    const fsLabel = fs.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    const itemsLbl = `${counts.items} ${counts.items === 1 ? 'item' : 'items'}`;
    const prodLbl = `${counts.products} ${counts.products === 1 ? 'product' : 'products'}`;
    const refundList = (order.refunds || [])
        .map(r => ({ id: r.id, note: (r.note || '').trim(), amt: num(r.totalRefundedSet) }))
        .filter(r => r.amt > 0);
    const Row = ({ label, mid, val, cls = '', title }) => (
        <div className={`odv3-trow ${cls}`}>
            <span className="odv3-tlabel">{label}</span>
            <span className="odv3-tmid" title={title}>{mid}</span>
            <span className="odv3-tval">{val}</span>
        </div>
    );
    return (
        <div className="odv3-summary">
            {fs && (
                <div className="odv3-sum-status">
                    <span className={`odv3-badge odv3-badge--${FIN_TONE[fs] || 'ful'}`}>{fsLabel}</span>
                </div>
            )}
            <div className="odv3-totals">
                <Row label="Subtotal" mid={`${prodLbl} · ${itemsLbl}`} val={fmtNum(subtotalGross)}/>
                {discount > 0 && (
                    <>
                        <Row label="Discount" val={`−${fmt(order.currentTotalDiscountsSet)}`}
                             mid={<span className="odv3-sum-badges">{discountApps.map((d, i) => (
                                 <span key={i} className="odv3-badge odv3-badge--neu">{d.code || d.title || '—'}</span>
                             ))}</span>}/>
                        {(order.package_discount_details || []).map((pd, i) => (
                            <Row key={`pkgd-${i}`} label="↳" cls="odv3-trow--sub"
                                 mid={`${pd.title || 'Package'}${(pd.player || pd.team) ? ` — ${[pd.player, pd.team].filter(Boolean).join(' · ')}` : ''}`}
                                 val={`${pd.discount != null ? `−${fmtNum(Number(pd.discount))}` : ''}${pd.prorated ? ` (package ${fmtNum(Number(pd.package_discount))})` : ''}`}/>
                        ))}
                    </>
                )}
                {isPickup
                    ? <Row label="Pickup" mid={pickupLocationName} val={fmt(order.totalShippingPriceSet)}/>
                    : <Row label="Shipping" mid={order.shippingLine?.title || ''} val={fmt(order.totalShippingPriceSet)}/>}
                {taxLines.length > 0
                    ? taxLines.map((tl, i) => (
                        <Row key={i} label={i === 0 ? 'Taxes' : ''}
                             mid={`${tl.title || ''} ${tl.ratePercentage != null ? `${tl.ratePercentage}%` : ''}`.trim()}
                             val={fmt(tl.priceSet)}/>
                    ))
                    : (tax > 0 && <Row label="Taxes" mid="" val={fmt(order.totalTaxSet)}/>)}
                <Row label="Total" mid="" val={fmt(order.totalPriceSet)} cls="odv3-trow--grand"/>
            </div>
            {(received > 0 || refunded > 0) && (
                <div className="odv3-totals odv3-totals--paid">
                    {received > 0 && <Row label="Paid" mid="" val={fmt(order.totalReceivedSet)}/>}
                    {refunded > 0 && (refundList.length === 0
                        ? <Row label="Refunded" mid="" val={`−${fmt(order.totalRefundedSet)}`} cls="odv3-trow--refund"/>
                        : refundList.map((r, i) => (
                            <Row key={r.id || i} label={i === 0 ? 'Refunded' : ''} mid={r.note} title={r.note}
                                 val={`−${fmtNum(r.amt)}`} cls="odv3-trow--refund"/>
                        )))}
                    {Math.abs((received - refunded) - total) > 0.005 && (
                        <Row label="Net" mid="" val={fmtNum(received - refunded)} cls="odv3-trow--grand"/>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── sidebar ──────────────────────────────────────────────────────────────────

function SidebarCards({ order }) {
    if (!order) return null;
    const c = order.customer;
    const displayName = c?.displayName
        || `${c?.firstName || ''} ${c?.lastName || ''}`.trim()
        || order.email || '—';
    const email = c?.email || order.email;
    const phone = c?.phone;
    const numOrders = c?.numberOfOrders;
    const sa = order.shippingAddress;
    // Final destination: store pickup (Tools override wins) and/or a shipping address.
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean);
    const pickupFOs = fos.filter(f => isPickupMethod(f.deliveryMethod?.methodType));
    const overrideLoc = (order.store_pickup_override || '').trim();
    const pickupName = overrideLoc
        || (pickupFOs[0] && (pickupFOs[0].deliveryMethod?.presentedName || pickupFOs[0].assignedLocation?.name))
        || '';
    const showAddress = !pickupName || (fos.length > pickupFOs.length && !overrideLoc);
    const tags = (order.tags || []).filter(t => t && t.trim());
    const note = String(order.note || '').trim();

    const attrs = order.customAttributes || [];
    const expectedLoc = attrs.find(a => a.key === '_soccer_fulfillment_location')?.value || '';
    let fulfillmentTime = '';
    try {
        const clubRaw = attrs.find(a => a.key === '_soccer_club')?.value;
        const snap = clubRaw ? (JSON.parse(clubRaw)?.fulfillment_property?.value || '') : '';
        const nums = String(snap).match(/\d+/g) || [];
        if (nums.length >= 2) fulfillmentTime = `${nums[0]}–${nums[1]} business days`;
        else if (nums.length === 1) fulfillmentTime = `${nums[0]} business days`;
    } catch (_) { /* malformed attr → no row */ }
    const crm = order.teamportal_crm || '';
    const SOURCES = { team_portal: 'Team Portal', tp_retail: 'Team Portal', tp_addon: 'Team Portal', tp_retail_addon: 'Team Portal' };
    const src = order._cache?.source;
    const source = SOURCES[src] || order.sourceName || (src ? String(src).replace(/_/g, ' ') : '');
    const infoRows = [['CRM', crm], ['Expected location', expectedLoc], ['Fulfillment time', fulfillmentTime], ['Source', source]]
        .filter(([, v]) => v);

    return (
        <>
            <div className="odv3-scard odv3-scard--top">
                <h3>Customer</h3>
                <div className="odv3-srow">
                    <PersonIco/>
                    <div>{displayName}{numOrders != null && <small>{numOrders} order{numOrders !== 1 ? 's' : ''}</small>}</div>
                </div>
                {email && <div className="odv3-srow"><EmailIco/><div>{email}</div></div>}
                {phone && <div className="odv3-srow"><PhoneIco/><div>{phone}</div></div>}
            </div>

            {(sa || pickupName) && (
                <div className="odv3-scard odv3-scard--top">
                    <h3>Ship to</h3>
                    {pickupName && (
                        <div className="odv3-srow">
                            <LocIco/>
                            <div>
                                {pickupName}
                                <small>Store pickup</small>
                            </div>
                        </div>
                    )}
                    {sa && showAddress && (
                        <div className="odv3-srow">
                            <LocIco/>
                            <div>
                                {sa.name || `${sa.firstName || ''} ${sa.lastName || ''}`.trim()}
                                {sa.address1 && <small>{sa.address1}{sa.address2 ? `, ${sa.address2}` : ''}</small>}
                                {(sa.city || sa.provinceCode) && (
                                    <small>{[sa.city, [sa.provinceCode || sa.province, sa.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</small>
                                )}
                                {sa.country && <small>{sa.country}</small>}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {infoRows.length > 0 && (
                <div className="odv3-scard">
                    <h3>Order info</h3>
                    <div className="odv3-iinfo">
                        {infoRows.map(([k, v]) => <React.Fragment key={k}><span className="odv3-ik">{k}</span><span className="odv3-iv">{v}</span></React.Fragment>)}
                    </div>
                </div>
            )}

            {note && (
                <div className="odv3-scard odv3-scard--note">
                    <h3>Order note</h3>
                    <div className="odv3-srow"><NoteIco/><div>{note}</div></div>
                </div>
            )}

            {tags.length > 0 && (
                <div className="odv3-scard">
                    <h3>Tags</h3>
                    <div className="odv3-tags">
                        {tags.map(t => <span key={t} className="odv3-badge odv3-badge--neu">{t}</span>)}
                    </div>
                </div>
            )}
        </>
    );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function OrderDetailV3() {
    const { orderId, storeSlug } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, emulating } = useAuth();
    const { activeStore, STORE_DISPLAY_NAMES: _storeNames } = useStore();
    const _storeFetch = useAuthFetch();
    const _fetchRef   = useRef(_storeFetch);
    _fetchRef.current = _storeFetch;

    const [order,       setOrder]       = useState(null);
    const [portalState, setPortalState] = useState({});
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [viewers,     setViewers]     = useState([]);

    const [activeTab,   setActiveTab]   = useState('ful');
    const [openBundles, setOpenBundles] = useState(new Set());
    const [preview, setPreview] = useState(null);
    const [groupBy,     setGroupBy]     = useState('order');
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const [visited,     setVisited]     = useState(() => new Set(['ful']));
    const [showRefund,  setShowRefund]  = useState(false);
    const [storedLabels, setStoredLabels] = useState([]);
    const [labelsNonce,  setLabelsNonce]  = useState(0);
    const [modifyMode,  setModifyMode]  = useState(false);
    const [pending,     setPending]     = useState(() => new Map());
    const [showReview,  setShowReview]  = useState(false);
    const [applying,    setApplying]    = useState(false);
    const [applyError,  setApplyError]  = useState(null);

    const [showPickupLoc,   setShowPickupLoc]   = useState(false);
    const [pickupLocList,   setPickupLocList]   = useState([]);
    const [pickupLocSel,    setPickupLocSel]    = useState('');
    const [pickupLocLoad,   setPickupLocLoad]   = useState(false);
    const [pickupLocSaving, setPickupLocSaving] = useState(false);
    const [pickupLocErr,    setPickupLocErr]    = useState(null);

    const [skuNotifications,   setSkuNotifications]   = useState({});
    const [numberPreviews,     setNumberPreviews]     = useState({});
    const [printoutFooterHtml, setPrintoutFooterHtml] = useState(null);
    const [printReady,        setPrintReady]        = useState(false);
    const [printBins,         setPrintBins]         = useState({});
    const [printOverstock,    setPrintOverstock]    = useState({});
    const [printPreviews,     setPrintPreviews]     = useState({});
    const [printFull,         setPrintFull]         = useState(false);
    const [showPrintConfirm,  setShowPrintConfirm]  = useState(false);
    const [showPrintOptions,  setShowPrintOptions]  = useState(false);
    const [showOnHoldBlock,   setShowOnHoldBlock]   = useState(false);
    const [mergeNj,           setMergeNjState]      = useState(() => readMergeNjPref());
    const [printMergedLocs,   setPrintMergedLocs]   = useState(null);
    const [printToast,        setPrintToast]        = useState(null);
    const printPopupRef  = useRef(null);
    const printSourceRef = useRef(null);
    const [reportOpen,  setReportOpen]  = useState(false);

    // ── fetch order + portal state ──────────────────────────────────────────
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        let cancelled = false;
        const f = _fetchRef.current;
        setLoading(true); setError(null);
        (async () => {
            try {
                const r = await f(`/api/orders/${orderId}`);
                if (!r.ok) throw new Error('Order not found');
                const data = await r.json();
                if (cancelled) return;
                setOrder(data);
                const ps = await f(`/api/orders/${orderId}/portal-state`)
                    .then(x => x.ok ? x.json() : {}).catch(() => ({}));
                if (!cancelled) setPortalState(ps || {});
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load order');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── presence heartbeat ──────────────────────────────────────────────────
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        const beat = () => {
            _fetchRef.current(`/api/orders/${orderId}/presence`, { method: 'POST' })
                .then(r => r.ok ? r.json() : [])
                .then(v => setViewers(Array.isArray(v) ? v : []))
                .catch(() => {});
        };
        beat();
        const t = setInterval(beat, 8000);
        return () => clearInterval(t);
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── print-card inputs ───────────────────────────────────────────────────
    useEffect(() => {
        if (!orderId || orderId === 'null') return;
        const f = _fetchRef.current;
        f(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
        f(`/api/orders/${orderId}/number-previews`)
            .then(r => r.ok ? r.json() : {})
            .then(j => setNumberPreviews(j?.previews || j || {}))
            .catch(() => setNumberPreviews({}));
        const sk = activeStore || storeSlug || 'soccerpost';
        f(`/api/store/printout-footer?store=${encodeURIComponent(sk)}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.html != null) setPrintoutFooterHtml(d.html); })
            .catch(() => {});
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── stored shipping labels (self-heals labels bought outside OpsHub) ────
    const labelsFs = order?.displayFulfillmentStatus || '';
    useEffect(() => {
        if (!orderId || orderId === 'null' || !order) return;
        let cancelled = false;
        (async () => {
            const f = _fetchRef.current;
            const fetchLabels = async () => {
                const r = await f(`/api/orders/${orderId}/shipping-labels`);
                if (!r.ok) return [];
                const d = await r.json();
                return d.labels || [];
            };
            try {
                let arr = await fetchLabels();
                const st = labelsFs.toUpperCase();
                const isShipped = st === 'FULFILLED' || st === 'PARTIALLY_FULFILLED';
                const canRecover = hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'store_manager']);
                if (arr.length === 0 && isShipped && canRecover) {
                    try {
                        await f(`/api/orders/${orderId}/shipping-labels/recover`, { method: 'POST' });
                        arr = await fetchLabels();
                    } catch (_) { /* keep empty */ }
                }
                if (!cancelled) setStoredLabels(arr);
            } catch (_) { if (!cancelled) setStoredLabels([]); }
        })();
        return () => { cancelled = true; };
    }, [orderId, labelsNonce, labelsFs, !!order]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── derived data ────────────────────────────────────────────────────────
    const storeKey = activeStore || storeSlug || 'soccerpost';

    const lineItems = useMemo(() => {
        const edges = order?.lineItems?.edges;
        if (Array.isArray(edges)) return edges.map(e => e.node).filter(Boolean);
        if (Array.isArray(order?.lineItems)) return order.lineItems;
        return [];
    }, [order]);

    const bundles = useMemo(() => {
        const built = buildBundles(lineItems, { tags: order?.tags });
        const isBroken = (order?.tags || []).some(t => (t || '').trim().toLowerCase() === 'broken order');
        if (!isBroken) return built;
        return built.map(b => ({
            ...b,
            children: (b.children || []).map(c =>
                c && (c.quantity || 0) > 1 ? { ...c, quantity: 1, currentQuantity: 1 } : c
            ),
        }));
    }, [lineItems, order?.tags]);

    const lineFlags = useMemo(() => {
        const out = {};
        for (const e of (order?.fulfillmentOrders?.edges || [])) {
            const flags = e?.node?._lineItemFlags;
            if (!flags) continue;
            for (const [lid, f] of Object.entries(flags)) {
                if (!out[lid]) { out[lid] = f; continue; }
                out[lid] = {
                    picked:  !!(out[lid].picked  || f.picked),
                    packed:  !!(out[lid].packed  || f.packed),
                    pressed: !!(out[lid].pressed || f.pressed),
                };
            }
        }
        return out;
    }, [order]);

    const fulfillmentShipped = !!(order && (
        order.displayFulfillmentStatus === 'FULFILLED'
        || order.displayFulfillmentStatus === 'PARTIALLY_FULFILLED'
        || order.fulfilled
        || order.fulfillmentStatus === 'fulfilled'
    ));
    const shippedLineIds = useMemo(() => {
        const ids = new Set();
        for (const f of (order?.fulfillments || [])) {
            if ((f?.status || '').toUpperCase() === 'CANCELLED') continue;
            for (const e of (f?.fulfillmentLineItems?.edges || [])) {
                const id = e?.node?.lineItem?.id;
                if (id) ids.add(id);
            }
        }
        return ids;
    }, [order]);

    const { splitBundleMap, hasSplitBundles } = useMemo(() => {
        const fosAll = (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean);
        const liToFoId = {};
        fosAll.forEach(fo => (fo.lineItems?.edges || []).forEach(e => { const id = e.node?.lineItem?.id; if (id) liToFoId[id] = fo.id; }));
        const groupFoSets = {};
        lineItems.forEach(li => {
            const g = li.lineItemGroup?.id; if (!g) return;
            const f = liToFoId[li.id]; if (!f) return;
            (groupFoSets[g] = groupFoSets[g] || new Set()).add(f);
        });
        const attrOf = (li, key) => (li.customAttributes || []).find(a => a.key === key)?.value;
        const parentByKey = {};
        lineItems.forEach(li => { const sk = attrOf(li, '_soccer_key'); if (sk) parentByKey[sk] = li; });
        const addonGroupKey = {};
        lineItems.forEach(li => {
            if (li.lineItemGroup?.id) return;
            const pk = attrOf(li, '_soccer_parent_line_item_key');
            const parent = pk ? parentByKey[pk] : null;
            if (!parent || parent.id === li.id) return;
            const fc = liToFoId[li.id], fp = liToFoId[parent.id];
            if (!fc || !fp) return;
            const native = parent.lineItemGroup?.id;
            const grpId = native || `tpkey:${parent.id}`;
            (groupFoSets[grpId] = groupFoSets[grpId] || new Set()).add(fp);
            groupFoSets[grpId].add(fc);
            addonGroupKey[li.id] = grpId;
            if (!native) addonGroupKey[parent.id] = grpId;
        });
        if (order?.jumbled_ignore_teamatical) {
            const tmGroups = new Set();
            lineItems.forEach(li => {
                const v = li.product?.teamaticalId?.value ?? li.variant?.product?.teamaticalId?.value;
                if (!v || !String(v).trim()) return;
                const g = li.lineItemGroup?.id || addonGroupKey[li.id];
                if (g) tmGroups.add(g);
            });
            tmGroups.forEach(g => { delete groupFoSets[g]; });
        }
        const map = {}; let idx = 0;
        Object.entries(groupFoSets).forEach(([g, set]) => { if (set.size > 1) { map[g] = idx % 6; idx++; } });
        Object.entries(addonGroupKey).forEach(([liId, g]) => { if (map[g] != null) map[liId] = map[g]; });
        return { splitBundleMap: map, hasSplitBundles: idx > 0 };
    }, [order, lineItems]);

    const refundedRows = useMemo(() => {
        const rows = [];
        for (const b of bundles) {
            for (const li of [b.parent, ...(b.children || [])]) {
                if (!li || isVirtual(li.title)) continue;
                const orig = li.originalQuantity ?? li.quantity ?? 0;
                const cur = li.quantity || 0;
                if (orig <= cur) continue;
                rows.push({ key: `__refunded__${li.id}`, refunded: true, children: [],
                    parent: { ...li, quantity: orig - cur, currentQuantity: orig - cur } });
            }
        }
        return rows;
    }, [bundles]);

    const roster = useMemo(() => parseRoster(order), [order]);
    const v3Ctx = useMemo(() => ({
        fulfillmentShipped, splitBundleMap, numberPreviews, shippedLineIds, roster, openPreview: setPreview,
    }), [fulfillmentShipped, splitBundleMap, numberPreviews, shippedLineIds, roster]);

    const summaryCounts = useMemo(() => {
        const live = (bundles || []).filter(b => b.parent && !b.refunded && !isVirtual(b.parent.title));
        return {
            products: live.length,
            items: (lineItems || []).reduce((n, li) => n + (parseInt(li.quantity || 0, 10) || 0), 0),
        };
    }, [bundles, lineItems]);

    useEffect(() => {
        if (!preview) return undefined;
        const k = (e) => { if (e.key === 'Escape') setPreview(null); };
        window.addEventListener('keydown', k);
        return () => window.removeEventListener('keydown', k);
    }, [preview]);

    const hasTpTag = (order?.tags || []).includes('Team Portal');
    const tpOverrides = useMemo(() => {
        const numById = {}, numByName = {}, teamById = {}, teamByName = {};
        const idx = (list, byId, byName) => {
            for (const ov of (list || [])) {
                if (!ov) continue;
                if (ov.player_id != null) byId[String(ov.player_id)] = ov;
                if (ov.player_name) byName[String(ov.player_name).trim().toLowerCase()] = ov;
            }
        };
        idx(order?.tp_number_overrides, numById, numByName);
        idx(order?.tp_team_overrides, teamById, teamByName);
        const byPlayer = (playerId, name) => {
            const k = name ? String(name).trim().toLowerCase() : '';
            return {
                number: (playerId != null && numById[String(playerId)]) || (k && numByName[k]) || null,
                team:   (playerId != null && teamById[String(playerId)]) || (k && teamByName[k]) || null,
            };
        };
        const any = Object.keys(numById).length + Object.keys(numByName).length
            + Object.keys(teamById).length + Object.keys(teamByName).length;
        return any ? { byPlayer } : null;
    }, [order]);

    const foSubSections = useMemo(() => order ? buildFoSubSections(order, storedLabels) : [], [order, storedLabels]);

    const tpReqByLiId = useMemo(() => {
        const out = {};
        for (const l of (order?.tp_requirements?.line_items || [])) {
            if (l?.line_item_gid) out[l.line_item_gid] = l;
        }
        return out;
    }, [order]);

    const tpPlayers = useMemo(() => {
        const m = new Map();
        for (const p of (order?.tp_requirements?.players || [])) {
            if (p?.user_id != null) m.set(String(p.user_id), p);
        }
        return m;
    }, [order]);

    const toggleGroup = useCallback((k) => setCollapsedGroups(prev => {
        const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
    }), []);
    const jumpToPlayer = useCallback((key) => {
        setCollapsedGroups(prev => {
            const n = new Set(prev);
            [...n].forEach(e => { if (e.endsWith(`|${key}`)) n.delete(e); });
            return n;
        });
        setTimeout(() => scrollToPlayerHeading(key), 0);
    }, []);
    const playerSections = useMemo(
        () => (groupBy === 'player' ? buildPlayerSections(bundles, roster, tpOverrides, tpPlayers) : []),
        [groupBy, bundles, roster, tpOverrides, tpPlayers]);

    const priceOf = useCallback((id) => {
        const li = lineItems.find(x => x.id === id);
        if (!li) return 0;
        const p = li.discountedTotalSet?.shopMoney?.amount
            ?? li.originalTotalSet?.shopMoney?.amount
            ?? li.discountedTotal?.amount
            ?? li.originalTotal?.amount
            ?? (li.variant?.price && parseFloat(li.variant.price) * (li.quantity || 1));
        const n = parseFloat(p);
        return isFinite(n) ? n : 0;
    }, [lineItems]);

    const pickPressTotals = useMemo(() => {
        const c = order?._cache || {};
        const itemsCount   = Math.max(0, parseInt(c.items_count        ?? 0, 10) || 0);
        const itemsPicked  = Math.max(0, parseInt(c.items_picked_total  ?? 0, 10) || 0);
        const itemsPacked  = Math.max(0, parseInt(c.items_packed_total  ?? 0, 10) || 0);
        const totalBundles = Math.max(0, parseInt(c.total_bundles       ?? 0, 10) || 0);
        const itemsPressed = Math.max(0, parseInt(c.items_pressed_total ?? 0, 10) || 0);
        let totalPackUnits = Math.max(0, parseInt(c.total_pack_units    ?? 0, 10) || 0);
        if (!totalPackUnits) {
            for (const b of (bundles || [])) {
                const it = b?.parent || {};
                if (isVirtual(it.title)) continue;
                if ((b.children || []).length > 0) { totalPackUnits += 1; continue; }
                if ((it.quantity || 0) === 0) continue;
                if (isDecoration(it)) continue;
                totalPackUnits += (it.quantity || 1);
            }
        }
        if (!totalPackUnits) totalPackUnits = itemsCount;
        const orderFullyShipped = (c.effective_fulfillment_status || order?.displayFulfillmentStatus || '')
            .toUpperCase() === 'FULFILLED';
        const pickedNum  = orderFullyShipped && itemsCount > 0     ? itemsCount     : Math.min(itemsPicked,  itemsCount);
        const packedNum  = orderFullyShipped && totalPackUnits > 0 ? totalPackUnits : Math.min(itemsPacked,  totalPackUnits);
        const pressedNum = orderFullyShipped && totalBundles > 0   ? totalBundles   : Math.min(itemsPressed, totalBundles);
        return { pickedNum, pickedDen: itemsCount, packedNum, packedDen: totalPackUnits,
                 pressedBundles: pressedNum, pressableBundles: totalBundles };
    }, [order, bundles]);

    // Pickup events: which part of the order a fulfillment order covers (base products in it of the order's base
    // products, the same count as the Packed header) and its Shopify fulfillment number(s).
    const pickupInfo = useCallback((foIds) => {
        const ids = new Set(foIds || []);
        let units = 0;
        const names = [];
        for (const ss of foSubSections) {
            if (!ids.has(ss.fo.id)) continue;
            if (ss.fulfillment?.name && !names.includes(ss.fulfillment.name)) names.push(ss.fulfillment.name);
            for (const b of (bundles || [])) {
                const p = b?.parent;
                if (!p || b.refunded || isVirtual(p.title) || !ss.lineItemIds.has(p.id) || (p.quantity || 0) === 0) continue;
                if ((b.children || []).length > 0) { units += 1; continue; }
                if (isDecoration(p)) continue;
                units += (ss.qtys?.[p.id] ?? p.quantity ?? 1);
            }
        }
        return { units, total: pickPressTotals.packedDen, names };
    }, [foSubSections, bundles, pickPressTotals]);

    const isOrderPrinted = useMemo(() =>
        !!order && (isPrintedTag(order.tags) || getPerLocationPrintTags(order.tags).length > 0),
    [order]);

    const refreshOrder = useCallback(async () => {
        try {
            const r = await _fetchRef.current(`/api/orders/${orderId}`);
            if (!r.ok) return;
            setOrder(await r.json());
        } catch { /* ignore */ }
    }, [orderId]);

    // ── Tools menu handlers ─────────────────────────────────────────────────
    const refreshPortalState = useCallback(async () => {
        try {
            const r = await _fetchRef.current(`/api/orders/${orderId}/portal-state`);
            if (!r.ok) return;
            setPortalState((await r.json()) || {});
        } catch { /* ignore */ }
    }, [orderId]);

    const openPickupLocModal = useCallback(async () => {
        setPickupLocErr(null);
        setPickupLocSel(order?.store_pickup_override || '');
        setShowPickupLoc(true);
        setPickupLocLoad(true);
        try {
            const res = await _fetchRef.current('/api/store-pickup-locations');
            const data = await res.json();
            setPickupLocList(data.locations || []);
        } catch (e) {
            setPickupLocErr('Failed to load location list: ' + e.message);
        } finally { setPickupLocLoad(false); }
    }, [order]);

    const savePickupLoc = useCallback(async (clear) => {
        if (!clear && !pickupLocSel) return;
        setPickupLocSaving(true); setPickupLocErr(null);
        try {
            const res = await _fetchRef.current(`/api/orders/${orderId}/store-pickup-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(clear ? { clear: true } : { location: pickupLocSel }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || `HTTP ${res.status}`);
            }
            setShowPickupLoc(false);
            await refreshOrder();
        } catch (e) { setPickupLocErr(e.message); }
        finally { setPickupLocSaving(false); }
    }, [orderId, pickupLocSel, refreshOrder]);

    const handleResync = useCallback(async () => {
        try {
            const res = await _fetchRef.current(`/api/orders/${orderId}/resync`, { method: 'POST' });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                alert(`Resync failed: ${d.detail || res.status}`);
                return;
            }
            await refreshOrder();
        } catch (e) { alert(`Resync failed: ${e.message}`); }
    }, [orderId, refreshOrder]);

    const handleToolAction = useCallback(async (action) => {
        const msgs = {
            reset_picking:  'Reset all picking history for this order? This cannot be undone.',
            reset_pressing: 'Reset all pressing history for this order? This cannot be undone.',
            reset_packing:  'Reset all packing history for this order? This cannot be undone.',
        };
        if (!window.confirm(msgs[action])) return;
        const groups = Object.entries(portalState || {})
            .filter(([k]) => k !== '_pressing_summary')
            .map(([group_key, state]) => ({ group_key, qty: state.qty_total || 1 }));
        try {
            const res = await _fetchRef.current(`/api/pressing/orders/${orderId}/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action, order_name: order?.name, groups }),
            });
            if (!res.ok) throw new Error('failed');
            await refreshPortalState();
        } catch { alert('Action failed. Please try again.'); }
    }, [orderId, order, portalState, refreshPortalState]);

    // ── Print pipeline ──────────────────────────────────────────────────────
    const isAdminOD = hasRole(user, ['admin']) || hasRole(user, ['customer_service']);
    const printLocationId = (() => {
        if (emulating?.shopify_location_id) return String(emulating.shopify_location_id);
        if (isAdminOD) return null;
        const mine = (user?.locations || [])
            .filter(l => (l.store_key || 'soccerpost') === (activeStore || 'soccerpost')
                      && l.shopify_location_id && String(l.shopify_location_id) !== '0');
        return mine.length === 1 ? String(mine[0].shopify_location_id) : null;
    })();
    const printLocationInScope = isAdminOD ? !!emulating?.shopify_location_id : !!printLocationId;

    const fetchBins = useCallback(async () => {
        const url = printLocationId
            ? `/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(printLocationId)}`
            : `/api/orders/${orderId}/bin-locations`;
        try {
            const res = await _fetchRef.current(url);
            if (!res.ok) return { bins: {}, overstock: {} };
            const d = await res.json();
            return { bins: d.bins || {}, overstock: d.overstock || {} };
        } catch { return { bins: {}, overstock: {} }; }
    }, [orderId, printLocationId]);

    const fetchPreviews = useCallback(async () => {
        try {
            const res = await _fetchRef.current(`/api/orders/${orderId}/number-previews`);
            if (!res.ok) return {};
            const d = await res.json();
            return d.previews || {};
        } catch { return {}; }
    }, [orderId]);

    const openLoadingPopup = useCallback(() => {
        const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
        if (!popup) { alert('Please allow popups for this site to enable printing.'); return null; }
        writePopupHTML(popup, '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>\n<body style="font-family:sans-serif;padding:40px;color:#374151"><p style="font-size:15px">Loading…</p></body></html>');
        return popup;
    }, []);

    const njLocsOnOrder = useMemo(() => njWarehouseLocsOnOrder(order, { all: printFull }), [order, printFull]);
    const njOffer = njMergeAllowed(storeKey) && njLocsOnOrder.length === 2 && !!printLocationId
        && njLocsOnOrder.some(l => String(l.id) === String(printLocationId));

    const loadPlainPrint = useCallback(() => {
        setPrintMergedLocs(null);
        Promise.all([fetchBins(), fetchPreviews()]).then(([b, previews]) => {
            setPrintBins(b.bins || {});
            setPrintOverstock(b.overstock || {});
            setPrintPreviews(previews);
            setPrintReady(true);
        });
    }, [fetchBins, fetchPreviews]);

    const startPrint = useCallback((merge) => {
        setShowPrintOptions(false);
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        if (merge) {
            Promise.all([
                Promise.all(njLocsOnOrder.map(async l => {
                    try {
                        const r = await _fetchRef.current(`/api/orders/${orderId}/bin-locations?shopify_location_id=${encodeURIComponent(l.id)}`);
                        const j = r.ok ? await r.json() : {};
                        return { ...l, bins: j.bins || {}, overstockBins: j.overstock || {} };
                    } catch { return { ...l, bins: {}, overstockBins: {} }; }
                })),
                fetchPreviews(),
            ]).then(([locs, previews]) => { setPrintMergedLocs(locs); setPrintPreviews(previews); setPrintReady(true); });
            return;
        }
        loadPlainPrint();
    }, [njLocsOnOrder, orderId, fetchPreviews, loadPlainPrint, openLoadingPopup]);

    const handlePrint = useCallback(() => {
        if (!printLocationInScope) {
            setPrintToast({ msg: "You can't print orders when you are viewing \"All Locations\". Choose a single location first.", key: Date.now() });
            return;
        }
        if (order?.displayFulfillmentStatus === 'ON_HOLD') { setShowOnHoldBlock(true); return; }
        if (njOffer) { setShowPrintOptions(true); return; }
        if (isPrintedTag(order?.tags) || getPerLocationPrintTags(order?.tags).length > 0) {
            setShowPrintConfirm(true);
            return;
        }
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        loadPlainPrint();
    }, [order?.tags, order?.displayFulfillmentStatus, printLocationInScope, njOffer, openLoadingPopup, loadPlainPrint]);

    const handlePrintConfirmed = useCallback(() => {
        setShowPrintConfirm(false);
        const popup = openLoadingPopup();
        if (!popup) return;
        printPopupRef.current = popup;
        loadPlainPrint();
    }, [openLoadingPopup, loadPlainPrint]);

    useEffect(() => {
        if (!printReady || !printSourceRef.current || !printPopupRef.current) return;
        const popup = printPopupRef.current;
        if (popup.closed) { setPrintReady(false); return; }
        let cancelled = false;
        const captureAndWrite = () => {
            if (cancelled) return;
            if (popup.closed) { setPrintReady(false); return; }
            writePopupHTML(popup, buildPopupHTML(printSourceRef.current.innerHTML));
            const sloc = emulating?.shopify_location_id || order?._shopify_location_id || null;
            const slocs = printMergedLocs ? printMergedLocs.map(l => l.id) : (sloc ? [sloc] : []);
            if (slocs.length) {
                _fetchRef.current(`/api/orders/${orderId}/printed_at_location`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ shopify_location_id: String(slocs[0]), shopify_location_ids: slocs.map(String), action: 'add' }),
                }).then(r => r.ok ? r.json() : null).then(d => {
                    if (d && Array.isArray(d.tags)) setOrder(prev => prev ? { ...prev, tags: d.tags } : prev);
                }).catch(() => {});
            }
            _fetchRef.current(`/api/orders/${orderId}/log-event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_type: isPrintedTag(order?.tags) ? 'order_reprint' : 'order_print',
                    order_name: order?.name,
                    portal_username: user?.user || null,
                    payload: { full_order: !!printFull, ...(printMergedLocs ? { merged_nj_warehouses: true, locations: printMergedLocs.map(l => l.name) } : {}) },
                }),
            }).catch(() => {});
            setPrintReady(false);
            setPrintMergedLocs(null);
            printPopupRef.current = null;
        };
        const imgs = Array.from(printSourceRef.current.querySelectorAll('img'));
        const pending = imgs.filter(i => !i.complete || i.naturalWidth === 0);
        if (pending.length === 0) {
            requestAnimationFrame(() => requestAnimationFrame(captureAndWrite));
        } else {
            let left = pending.length;
            const tick = () => { if (--left <= 0) requestAnimationFrame(() => requestAnimationFrame(captureAndWrite)); };
            pending.forEach(img => {
                img.addEventListener('load',  tick, { once: true });
                img.addEventListener('error', tick, { once: true });
            });
            setTimeout(() => { if (!cancelled) captureAndWrite(); }, 2000);
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printReady, orderId]);

    useEffect(() => {
        if (!printToast) return;
        const id = setTimeout(() => setPrintToast(null), 4000);
        return () => clearTimeout(id);
    }, [printToast]);

    // ── Modify pick/press ───────────────────────────────────────────────────
    const canModify = !allRolesIn(user, ['crm', 'finance', 'customer_service', 'store_manager', 'external']);

    const locIdByLiId = useMemo(() => {
        const m = {};
        for (const ss of foSubSections) {
            const loc = String(ss?.fo?.assignedLocation?.location?.id || '').split('/').pop() || '';
            for (const id of ss.lineItemIds) if (!(id in m)) m[id] = loc;
        }
        return m;
    }, [foSubSections]);

    const togglePending = useCallback(({ groupKey, stage, qty, title, currentState, locId, cascade }) => {
        const key = `${groupKey}::${stage}`;
        const pk = `${groupKey}::pressed`;
        setPending(prev => {
            const next = new Map(prev);
            if (next.has(key)) {
                next.delete(key);
                if (stage === 'picked' && next.get(pk)?.auto) next.delete(pk);
            } else {
                const desired = currentState !== 'done';
                next.set(key, { groupKey, stage, qty: qty || 1, title, desired, cur: currentState, locId: locId || '' });
                if (stage === 'picked' && !desired && cascade && !next.has(pk)
                    && (cascade.pressedState === 'done' || cascade.pressedState === 'partial')) {
                    next.set(pk, { groupKey, stage: 'pressed', qty: qty || 1, title, desired: false,
                        cur: cascade.pressedState, locId: locId || '', auto: true });
                }
            }
            return next;
        });
    }, []);

    const buildTargets = useCallback((list) => {
        const picked = [], pressed = [];
        for (const b of list) {
            const par = b.parent;
            if (!par?.sku || isVirtual(par.title)) continue;
            const locId = locIdByLiId[par.id] || '';
            const dots = dotsForBundle(par, b.children, portalState, fulfillmentShipped, lineFlags);
            picked.push({ groupKey: par.sku, qty: par.quantity || 1, title: par.title, currentState: dots.picked.state, locId });
            if ((b.children || []).some(c => isDecoration(c))) {
                pressed.push({ groupKey: par.sku, qty: par.quantity || 1, title: par.title, currentState: dots.pressed.state, locId });
            }
            for (const c of (b.children || [])) {
                if (!isDecoration(c) || !c.sku || isVirtual(c.title)) continue;
                const cps = psForSku(portalState, c.sku) || portalState[c.sku] || null;
                const cd = dotsForRow('decoration', c, cps, fulfillmentShipped, lineFlags?.[c.id]);
                picked.push({ groupKey: c.sku, qty: c.quantity || 1, title: c.title, currentState: cd.picked.state, locId });
            }
        }
        return { picked, pressed };
    }, [portalState, lineFlags, locIdByLiId, fulfillmentShipped]);

    const selectAllTargets = useMemo(
        () => (modifyMode ? buildTargets(bundles) : { picked: [], pressed: [] }),
        [modifyMode, bundles, buildTargets]);

    const selectAllStage = useCallback((stage, targets) => {
        if (!targets.length) return;
        setPending(prev => {
            let effDone = 0;
            for (const t of targets) {
                const p = prev.get(`${t.groupKey}::${stage}`);
                if (p ? p.desired : t.currentState === 'done') effDone++;
            }
            const desired = effDone < targets.length;
            const next = new Map();
            for (const [k, v] of prev) if (!k.endsWith(`::${stage}`)) next.set(k, v);
            for (const t of targets) {
                if ((t.currentState === 'done') !== desired) {
                    next.set(`${t.groupKey}::${stage}`, { groupKey: t.groupKey, stage, qty: t.qty || 1,
                        title: t.title, desired, cur: t.currentState, locId: t.locId || '' });
                }
            }
            return next;
        });
    }, []);

    const stageState = useCallback((targets, stage) => {
        if (!targets.length) return 'todo';
        let done = 0;
        for (const t of targets) {
            const p = pending.get(`${t.groupKey}::${stage}`);
            if (p ? p.desired : t.currentState === 'done') done++;
        }
        return done === targets.length ? 'done' : done === 0 ? 'todo' : 'partial';
    }, [pending]);

    const setAll = useCallback((desired) => {
        const next = new Map();
        for (const stage of ['picked', 'pressed']) {
            for (const t of selectAllTargets[stage]) {
                if ((t.currentState === 'done') !== desired) {
                    next.set(`${t.groupKey}::${stage}`, { groupKey: t.groupKey, stage, qty: t.qty || 1,
                        title: t.title, desired, cur: t.currentState, locId: t.locId || '' });
                }
            }
        }
        setPending(next);
    }, [selectAllTargets]);

    const livePickPress = useMemo(() => {
        if (!modifyMode || pending.size === 0) return pickPressTotals;
        let pk = 0, pr = 0;
        for (const e of pending.values()) {
            const delta = e.desired ? (e.cur === 'done' ? 0 : 1) : (e.cur === 'done' ? -1 : 0);
            if (e.stage === 'picked') pk += delta * (e.qty || 1);
            else pr += delta;
        }
        const clamp = (v, max) => Math.max(0, Math.min(max, v));
        return { ...pickPressTotals,
            pickedNum: clamp(pickPressTotals.pickedNum + pk, pickPressTotals.pickedDen),
            pressedBundles: clamp(pickPressTotals.pressedBundles + pr, pickPressTotals.pressableBundles) };
    }, [modifyMode, pending, pickPressTotals]);

    const exitModify = useCallback(() => {
        setPending(new Map()); setModifyMode(false); setShowReview(false); setApplyError(null);
    }, []);

    const applyPending = useCallback(async () => {
        if (pending.size === 0 || applying) return;
        setApplying(true); setApplyError(null);
        const pageLoc = String(searchParams.get('loc') || '').trim();
        const buckets = new Map();
        for (const { groupKey, stage, qty, desired, locId } of pending.values()) {
            const verb = stage === 'picked'
                ? (desired ? 'mark_selected_picked' : 'mark_selected_not_picked')
                : (desired ? 'mark_selected_pressed' : 'mark_selected_not_pressed');
            const loc = locId || pageLoc;
            if (!loc) {
                setApplyError('A fulfillment has no assigned location, so items cannot be marked. Reload the order and try again.');
                setApplying(false);
                return;
            }
            const k = `${verb}|${loc}`;
            if (!buckets.has(k)) buckets.set(k, { action: verb, loc, groups: [] });
            buckets.get(k).groups.push({ group_key: groupKey, qty, total_qty: qty });
        }
        try {
            for (const { action, loc, groups } of buckets.values()) {
                const res = await _fetchRef.current(`/api/pressing/orders/${orderId}/tools`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action, order_name: order?.name, groups, shopify_location_id: loc }),
                });
                if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(`${action} failed (HTTP ${res.status})${j.detail ? `: ${j.detail}` : ''}`);
                }
            }
            await refreshPortalState();
            await refreshOrder();
            exitModify();
        } catch (e) {
            setApplyError(e.message);
            await refreshPortalState();
        } finally { setApplying(false); }
    }, [pending, applying, searchParams, orderId, order, refreshPortalState, refreshOrder, exitModify]);

    const foMenuCtx = useMemo(() => ({
        orderId,
        storeKey: order?._store_key || storeKey,
        storeFetch: _storeFetch,
        storedLabels,
        storePickupOverride: order?.store_pickup_override || '',
        siblings: (order?.fulfillmentOrders?.edges || []).map(e => e.node).filter(Boolean).map(sib => ({
            id: sib.id, status: sib.status,
            locationName: sib.assignedLocation?.name || '',
            locationId: (sib.assignedLocation?.location?.id || '').split('/').pop() || '',
        })),
        onAfterAction: async () => { await refreshOrder(); await refreshPortalState(); setLabelsNonce(n => n + 1); },
        onToast: (t) => setPrintToast(t),
    }), [orderId, order, storeKey, _storeFetch, storedLabels, refreshOrder, refreshPortalState]);

    const enterModify = useCallback(() => {
        setOpenBundles(new Set(bundles.filter(b => (b.children || []).length > 0).map(b => b.parent?.id).filter(Boolean)));
        setModifyMode(true);
    }, [bundles]);

    const modify = useMemo(() => ({
        on: modifyMode, pending, toggle: togglePending, locIdByLiId,
        targetsFor: buildTargets, selectStage: selectAllStage, stageState,
    }), [modifyMode, pending, togglePending, locIdByLiId, buildTargets, selectAllStage, stageState]);

    const selectTab = useCallback((id) => {
        setActiveTab(id);
        setVisited(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
    }, []);

    const onToggleBundle = useCallback((id) => {
        setOpenBundles(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const backTo = useMemo(() => resolveOrdersBackTo(null, activeStore || storeSlug, null), [activeStore, storeSlug]);
    // Prev/next — ordered id list saved by the Orders list (same cursor V2 reads).
    const prevNext = useMemo(() => {
        const sk = activeStore || storeSlug || 'soccerpost';
        try {
            const env = JSON.parse(sessionStorage.getItem('ordersList') || 'null');
            const ids = Array.isArray(env?.ids) ? env.ids : [];
            if ((env?.store || sk) !== sk) return { ids: [], idx: -1, store: sk };
            return { ids, idx: ids.indexOf(String(orderId)), store: sk };
        } catch { return { ids: [], idx: -1, store: sk }; }
    }, [orderId, activeStore, storeSlug]);
    const prevOrderId = prevNext.idx > 0 ? prevNext.ids[prevNext.idx - 1] : null;
    const nextOrderId = (prevNext.idx >= 0 && prevNext.idx < prevNext.ids.length - 1) ? prevNext.ids[prevNext.idx + 1] : null;
    const goAdjacent = (id) => {
        if (id) navigate(`/store/${prevNext.store}/orders/${id}?v=3`, { state: { backTo } });
    };

    // ── display helpers ─────────────────────────────────────────────────────
    const orderName = order?.name || order?.legacyResourceId || (orderId ? `#${orderId}` : '—');
    const createdAt = order?.createdAt ? new Date(order.createdAt) : null;
    const foEdges   = order?.fulfillmentOrders?.edges || [];
    const foFulfillBys = foEdges.map(e => e?.node?.fulfillBy).filter(Boolean).sort();
    const fulfillByRaw = order?._fulfill_by || foFulfillBys[0] || null;
    const fulfillBy    = fulfillByRaw ? new Date(fulfillByRaw) : null;

    const fulfillStatus = (order?.displayFulfillmentStatus || '').toUpperCase();
    const payStatus     = (order?.displayFinancialStatus   || '').toUpperCase();

    const fmtDate      = d => d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const fmtDateShort = d => d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    const fulfillLabel = { UNFULFILLED: 'Unfulfilled', PARTIALLY_FULFILLED: 'Partially fulfilled',
                           FULFILLED: 'Fulfilled', RESTOCKED: 'Restocked' }[fulfillStatus] || fulfillStatus || 'Unknown';
    const fulfillCls   = { FULFILLED: 'odv3-badge--ful', PARTIALLY_FULFILLED: 'odv3-badge--par',
                           UNFULFILLED: 'odv3-badge--unf' }[fulfillStatus] || 'odv3-badge--unf';
    const payLabel     = { PAID: 'Paid', PARTIALLY_PAID: 'Partially paid',
                           PARTIALLY_REFUNDED: 'Partially refunded', REFUNDED: 'Refunded',
                           PENDING: 'Pending', VOIDED: 'Voided' }[payStatus] || payStatus || '—';



    const playerCount = useMemo(() => {
        const { playerBundles, retailBundles } = splitBundlesByCategory(bundles, roster);
        return groupByPlayer(playerBundles, roster).length + (retailBundles.length ? 1 : 0);
    }, [bundles, roster]);
    const clubs = useMemo(() => {
        const set = new Set();
        for (const b of bundles) {
            const club = getPlayer(b.parent, roster).club;
            if (club) set.add(club);
        }
        return Array.from(set);
    }, [bundles, roster]);

    // ── render ──────────────────────────────────────────────────────────────
    if (loading) return <div className="odv3-load">Loading…</div>;
    if (error)   return <div className="odv3-err">Error: {error}</div>;
    if (!order)  return null;

    return (
        <StoreKeyContext.Provider value={order?._store_key || storeKey}>
        <SkuNotificationsContext.Provider value={skuNotifications}>
        <V3Ctx.Provider value={v3Ctx}>
        <FoMenuCtx.Provider value={foMenuCtx}>
        <div className="odv3">
            {/* ── Sticky header ─────────────────────────────────────────── */}
            <header className="odv3-head">
                <div className="odv3-crumb">
                    <button className="odv3-crumb-btn"
                            onClick={() => navigate(backTo || `/${storeKey}/orders`)}>
                        <ChevL/> Orders
                    </button>
                    <span className="odv3-crumb-sp"/>
                    <button className="odv3-crumb-nav" aria-label="Previous order"
                            title={prevOrderId ? 'Previous order' : 'No previous order in this list'}
                            disabled={!prevOrderId} onClick={() => goAdjacent(prevOrderId)}><ChevL/></button>
                    <button className="odv3-crumb-nav" aria-label="Next order"
                            title={nextOrderId ? 'Next order' : 'No next order in this list'}
                            disabled={!nextOrderId} onClick={() => goAdjacent(nextOrderId)}><ChevR/></button>
                </div>
                <div className="odv3-ph">
                    <div className="odv3-phL">
                        <div className="odv3-title-row">
                            <h1 className="odv3-h1">{orderName}</h1>
                            <span className={`odv3-badge ${order.cancelledAt ? 'odv3-badge--can' : fulfillCls}`}>
                                <span className="odv3-badge-d"/>
                                {order.cancelledAt ? 'Cancelled' : fulfillLabel}
                            </span>
                            <span className="odv3-badge odv3-badge--neu">{payLabel}</span>
                            {viewers.some(v => v?.user_id) && (
                                <div className="odv3-presence" aria-label="Also viewing">
                                    {viewers.filter(v => v?.user_id).slice(0, 6).map(v => (
                                        <div key={v.user_id} className={`odv3-av${v.is_me ? ' odv3-av--me' : ''}`}
                                             title={v.display_name || v.name || v.initials || ''}>
                                            {v.initials || '?'}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="odv3-meta">
                            {createdAt && <span>{fmtDate(createdAt)}</span>}
                            {fulfillBy && <><span className="odv3-meta-dot"/><span>Fulfill by <strong>{fmtDateShort(fulfillBy)}</strong></span></>}
                            {clubs.map(cl => <React.Fragment key={cl}><span className="odv3-meta-dot"/><span className="odv3-badge odv3-badge--club">{cl}</span></React.Fragment>)}
                        </div>
                    </div>
                    <div className="odv3-actions">
                        <ViewInMenu user={user} order={order} orderId={orderId} storeKey={storeKey}/>
                        {!allRolesIn(user, ['crm', 'finance', 'store_manager', 'external']) && (
                            <ToolsMenu user={user} onPickup={openPickupLocModal}
                                       onToolAction={handleToolAction} onResync={handleResync}/>
                        )}
                        <OrderNotificationsControl
                            iconOnly
                            storeKey={storeKey}
                            orderId={orderId}
                            orderName={order?.name}
                            activeNotifications={order?.active_notifications}
                            onChange={refreshOrder}
                        />
                        {hasRole(user, ['admin', 'site_manager', 'customer_service', 'franchise', 'user']) && (
                            <button className="odv3-btn odv3-btn--ico" title="Report an issue with this order"
                                    aria-label="Report issue" onClick={() => setReportOpen(true)}>
                                <FlagIco/>
                            </button>
                        )}
                        {hasRole(user, ['admin', 'franchise', 'site_manager', 'user', 'customer_service', 'store_manager']) && (
                            <button className="odv3-btn" title="Issue a refund for one or more items on this order"
                                    onClick={() => setShowRefund(true)}>
                                Refund
                            </button>
                        )}
                        {!allRolesIn(user, ['crm', 'finance', 'customer_service', 'external']) && (
                            <button className={`odv3-btn odv3-btn--primary${!printLocationInScope ? ' odv3-btn--soft-disabled' : ''}`}
                                    onClick={handlePrint}
                                    title={!printLocationInScope ? "You can't print orders when you are viewing \"All Locations\". Choose a single location first." : undefined}>
                                <PrintBtnIco printed={isOrderPrinted}/>Print order
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* ── Page body ─────────────────────────────────────────────── */}
            <div className="odv3-body">
                {/* Cancelled banner (same wording as V1) */}
                {order.cancelledAt && (
                    <div className="odv3-notif" role="alert">
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.7"/><path d="M5 15 15 5" stroke="currentColor" strokeWidth="1.7"/></svg>
                        <div className="odv3-notif-body">
                            <div className="odv3-notif-h">This order has been cancelled</div>
                            <div className="odv3-notif-m">
                                {[order.cancelReason && `Reason: ${CANCEL_REASON_LABELS[order.cancelReason] || String(order.cancelReason).replace(/_/g, ' ').toLowerCase()}`,
                                  fmtDate(new Date(order.cancelledAt))].filter(Boolean).join(' · ')}
                            </div>
                        </div>
                    </div>
                )}

                {/* Notification banner */}
                {Array.isArray(order.active_notifications) && order.active_notifications.length > 0 && (
                    <NotifBanner notifications={order.active_notifications}/>
                )}

                {hasSplitBundles && (
                    <div className="odv3-jumble" role="alert">
                        <strong>Jumbled bundle alert</strong>
                        <span>One or more bundles are split across shipments. Color-coded badges mark the affected items.</span>
                    </div>
                )}

                {/* Stages strip */}
                <StagesStrip pickPressTotals={livePickPress} isOrderPrinted={isOrderPrinted}/>

                {/* Main grid */}
                <div className="odv3-grid">

                    {/* Left column */}
                    <div className="odv3-col">
                        <div className="odv3-card odv3-card--main">
                            {/* Tab bar */}
                            <div className="odv3-tabs" role="tablist">
                                {[
                                    { id: 'ful', label: 'Fulfillments', count: foSubSections.length, icon: <TabIcoList/> },
                                    { id: 'ply', label: (hasTpTag || roster.size > 0) ? 'Players' : 'Items', count: playerCount, icon: <PersonIco/> },
                                    { id: 'vt',  label: 'Visual timeline', icon: <TabIcoTimeline/> },
                                    { id: 'log', label: 'Order event log', icon: <TabIcoLog/> },
                                ].map(({ id, label, count, icon }) => (
                                    <button key={id} role="tab"
                                            className={`odv3-tab${activeTab === id ? ' odv3-tab--on' : ''}`}
                                            aria-selected={activeTab === id}
                                            onClick={() => selectTab(id)}>
                                        {icon}{label}
                                        {count > 0 && <span className="odv3-tcount">{count}</span>}
                                    </button>
                                ))}
                                {(activeTab === 'ful' || activeTab === 'ply') && (
                                    <div className="odv3-tab-tools">
                                        {activeTab === 'ply' && canModify && !modifyMode && (
                                            <button className="odv3-btn odv3-btn--slim" onClick={enterModify}>Mod. pick/press</button>
                                        )}
                                        <button className="odv3-btn odv3-btn--slim"
                                                onClick={() => setOpenBundles(openBundles.size > 0
                                                    ? new Set()
                                                    : new Set(bundles.filter(b => (b.children || []).length > 0).map(b => b.parent?.id).filter(Boolean)))}>
                                            {openBundles.size > 0 ? 'Collapse all' : 'Expand all'}
                                        </button>
                                        {activeTab === 'ful' && (
                                            <div className="odv3-seg-grp">
                                                {['order', 'product', 'player'].map(g => (
                                                    <button key={g}
                                                            className={`odv3-seg-btn${groupBy === g ? ' odv3-seg-btn--on' : ''}`}
                                                            onClick={() => setGroupBy(g)}>
                                                        {g.charAt(0).toUpperCase() + g.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {modifyMode && (
                                <div className="odv3-modbar" role="region" aria-label="Modify pick and press">
                                    <strong>Modify pick/press</strong>
                                    <span className="odv3-modbar-hint">
                                        Click dots to stage changes{pending.size > 0 && <> · <strong>{pending.size}</strong> pending</>}
                                    </span>
                                    <span className="odv3-fo-sp"/>
                                    <button type="button" className="odv3-btn odv3-btn--slim" disabled={applying}
                                            onClick={() => setAll(true)}>Select all</button>
                                    <button type="button" className="odv3-btn odv3-btn--slim" disabled={applying}
                                            onClick={() => setAll(false)}>Clear all</button>
                                    <button type="button" className="odv3-btn odv3-btn--slim" disabled={applying} onClick={exitModify}>Cancel</button>
                                    <button type="button" className="odv3-btn odv3-btn--slim odv3-btn--primary"
                                            disabled={pending.size === 0 || applying}
                                            onClick={() => { setApplyError(null); setShowReview(true); }}>
                                        Review{pending.size ? ` (${pending.size})` : ''}
                                    </button>
                                </div>
                            )}

                            {/* Fulfillments tab */}
                            {activeTab === 'ful' && (
                                <>
                                    {groupBy === 'player' && playerSections.length > 1 && (
                                        <PlayerCards sections={playerSections} onPick={jumpToPlayer}/>
                                    )}
                                    {foSubSections.map(ss => (
                                        <FoSection
                                            key={ss.key}
                                            subSection={ss}
                                            order={order}
                                            onModify={enterModify}
                                            bundles={bundles}
                                            roster={roster}
                                            groupBy={groupBy}
                                            tpOv={tpOverrides}
                                            collapsedGroups={collapsedGroups}
                                            onToggleGroup={toggleGroup}
                                            modify={modify}
                                            portalState={portalState}
                                            lineFlags={lineFlags}
                                            priceOf={priceOf}
                                            tpReqByLiId={tpReqByLiId}
                                            openBundles={openBundles}
                                            onToggleBundle={onToggleBundle}
                                        />
                                    ))}
                                    {refundedRows.length > 0 && (
                                        <div className="odv3-fo">
                                            <div className="odv3-fo-head">
                                                <span className="odv3-fo-loc">Refunded / removed items</span>
                                                <span className="odv3-badge odv3-badge--sm odv3-badge--unf">
                                                    <span className="odv3-badge-d"/>Refunded
                                                </span>
                                            </div>
                                            <ItemsTable bundles={refundedRows} portalState={portalState} lineFlags={lineFlags}
                                                priceOf={priceOf} tpReqByLiId={tpReqByLiId}
                                                openBundles={openBundles} onToggleBundle={onToggleBundle}/>
                                        </div>
                                    )}
                                </>
                            )}

                            {activeTab === 'ply' && (
                                <PlayersTab modify={modify} bundles={bundles} roster={roster} hasTpTag={hasTpTag}
                                    tpOv={tpOverrides} tpPlayers={tpPlayers}
                                    portalState={portalState} lineFlags={lineFlags} priceOf={priceOf}
                                    tpReqByLiId={tpReqByLiId} openBundles={openBundles}
                                    onToggleBundle={onToggleBundle}/>
                            )}
                            {visited.has('vt') && (
                                <div className="odv3-tl" style={{ display: activeTab === 'vt' ? '' : 'none' }}>
                                    <VisualTimelineV3 orderId={orderId} order={order} fulfillBy={fulfillByRaw}
                                        fetchFn={_storeFetch} pickupInfo={pickupInfo}/>
                                </div>
                            )}
                            {visited.has('log') && (
                                <div className="odv3-log" style={{ display: activeTab === 'log' ? '' : 'none' }}>
                                    <EventLogV3 orderId={orderId} order={order} cancelReason={order.cancelReason}
                                        fetchFn={_storeFetch} pickupInfo={pickupInfo}/>
                                </div>
                            )}
                        </div>

                        <div className="odv3-card odv3-card--sum">
                            <div className="odv3-card-h">
                                <h2>Order summary</h2>
                                <span className="odv3-card-sub">{summaryCounts.products} product{summaryCounts.products === 1 ? '' : 's'} · {summaryCounts.items} item{summaryCounts.items === 1 ? '' : 's'}</span>
                            </div>
                            <OrderSummary order={order} lineItems={lineItems} counts={summaryCounts}/>
                        </div>
                    </div>

                    {/* Right sidebar */}
                    <div className="odv3-col odv3-col--side">
                        <SidebarCards order={order}/>
                    </div>

                </div>
            </div>

            {(() => { const css = buildFontFaceCss(numberPreviews); return css ? <style>{css}</style> : null; })()}
            <div ref={printSourceRef} className="od-print-clone" aria-hidden="true">
                {printMergedLocs ? (
                    <MergedNjPrint
                        fo={{ order: {}, location_name: order?._location_name || '' }}
                        fullOrder={order}
                        locs={printMergedLocs}
                        printAll={printFull}
                        numberPreviews={printPreviews}
                        storeKey={storeKey}
                        storeDisplayName={_storeNames?.[storeKey] || 'Soccer Post'}
                        printoutFooterHtml={printoutFooterHtml}
                        skuNotifications={skuNotifications}
                    />
                ) : (
                    <OrderPrintCard
                        fo={{ order: {}, location_name: order?._location_name || '' }}
                        fullOrder={order}
                        printAll={printFull}
                        bins={printBins}
                        overstockBins={printOverstock}
                        numberPreviews={printPreviews}
                        locationId={printLocationId}
                        storeKey={storeKey}
                        storeDisplayName={_storeNames?.[storeKey] || 'Soccer Post'}
                        printoutFooterHtml={printoutFooterHtml}
                        skuNotifications={skuNotifications}
                    />
                )}
            </div>

            {showPrintConfirm && (
                <div className="odv3-modal-bd" onClick={() => setShowPrintConfirm(false)}>
                    <div className="odv3-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv3-modal-t">Order already printed</div>
                        <div className="odv3-modal-b">
                            This order has already been printed. Are you sure you want to print it again?
                            <div style={{ marginTop: 12 }}>
                                <Checkbox label="Reprint full order, not just unfulfilled items"
                                          helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                                          checked={printFull} onChange={setPrintFull}/>
                            </div>
                        </div>
                        <div className="odv3-modal-a">
                            <button className="odv3-btn" onClick={() => setShowPrintConfirm(false)}>Cancel</button>
                            <button className="odv3-btn odv3-btn--primary" onClick={handlePrintConfirmed}>Print again</button>
                        </div>
                    </div>
                </div>
            )}

            {showPrintOptions && (
                <div className="odv3-modal-bd" onClick={() => setShowPrintOptions(false)}>
                    <div className="odv3-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv3-modal-t">Print order</div>
                        <div className="odv3-modal-b">
                            {isOrderPrinted && <div style={{ marginBottom: 10 }}>This order has already been printed. Printing it again.</div>}
                            <div style={{ fontWeight: 600, marginBottom: 12 }}>{order?.replacement_alias || order?.name}</div>
                            <Checkbox label="Merge NJ Warehouses"
                                      helpText="One ticket: the Tinton Falls Warehouse items first, a dividing line, then the Eatontown Warehouse items. The order is tagged as printed at both warehouses."
                                      checked={mergeNj}
                                      onChange={(on) => { setMergeNjState(on); writeMergeNjPref(on); }}/>
                            {isOrderPrinted && (
                                <div style={{ marginTop: 12 }}>
                                    <Checkbox label="Reprint full order, not just unfulfilled items"
                                              helpText="Every line at its ordered quantity — logos and all — even those already fulfilled."
                                              checked={printFull} onChange={setPrintFull}/>
                                </div>
                            )}
                        </div>
                        <div className="odv3-modal-a">
                            <button className="odv3-btn" onClick={() => setShowPrintOptions(false)}>Cancel</button>
                            <button className="odv3-btn odv3-btn--primary" onClick={() => startPrint(mergeNj)}>Print</button>
                        </div>
                    </div>
                </div>
            )}

            {showOnHoldBlock && (
                <div className="odv3-modal-bd" onClick={() => setShowOnHoldBlock(false)}>
                    <div className="odv3-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="odv3-modal-t">Order is on hold</div>
                        <div className="odv3-modal-b">
                            This order is <strong>on hold in Shopify</strong> and cannot be printed. Release the hold in Shopify, then come back to print.
                        </div>
                        <div className="odv3-modal-a">
                            <button className="odv3-btn odv3-btn--primary" onClick={() => setShowOnHoldBlock(false)}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            {printToast && <div className="odv3-toast" role="status">{printToast.msg}</div>}

            <Modal
                open={showPickupLoc}
                onClose={() => setShowPickupLoc(false)}
                title="Change Pickup Location"
                primaryAction={{
                    content: pickupLocSaving ? 'Saving…' : 'Save',
                    onAction: () => savePickupLoc(false),
                    disabled: !pickupLocSel || pickupLocSaving || pickupLocLoad,
                    loading: pickupLocSaving,
                }}
                secondaryActions={[
                    ...(order?.store_pickup_override && pickupLocSel === order.store_pickup_override ? [{
                        content: 'Clear override / Use original',
                        onAction: () => savePickupLoc(true),
                        disabled: pickupLocSaving,
                    }] : []),
                    { content: 'Cancel', onAction: () => setShowPickupLoc(false) },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {pickupLocErr && (
                            <Banner tone="critical" onDismiss={() => setPickupLocErr(null)}>
                                <Text as="p" variant="bodyMd">{pickupLocErr}</Text>
                            </Banner>
                        )}
                        {pickupLocLoad ? (
                            <Text as="p" variant="bodyMd" tone="subdued">Loading locations…</Text>
                        ) : pickupLocList.length === 0 ? (
                            <Banner tone="warning">
                                <Text as="p" variant="bodyMd">None of your locations is set up as a pickup location yet. Ask Soccer Post to add it.</Text>
                            </Banner>
                        ) : (
                            <Select label="Pickup location" placeholder="Choose a location…"
                                    options={pickupLocList.map(loc => ({ label: loc, value: loc }))}
                                    value={pickupLocSel} onChange={setPickupLocSel}/>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {showReview && (
                <div className="odv3-modal-bd" onClick={() => { if (!applying) setShowReview(false); }}>
                    <div className="odv3-modal odv3-modal--wide" onClick={(e) => e.stopPropagation()}>
                        <div className="odv3-modal-t">Review changes to {order?.name}</div>
                        <div className="odv3-modal-b">
                            <div className="odv3-rv-note">These changes write pick/press state and are logged to the order's event history.</div>
                            <div className="odv3-rv-list">
                                <table className="odv3-tbl">
                                    <thead><tr><th>Item</th><th>SKU</th><th>Stage</th><th>Change</th></tr></thead>
                                    <tbody>
                                        {Array.from(pending.values()).map(c => (
                                            <tr key={`${c.groupKey}::${c.stage}`}>
                                                <td>{c.title || '—'}</td>
                                                <td><code className="odv3-sku">{c.groupKey}</code></td>
                                                <td>{c.stage === 'picked' ? 'Picked' : 'Pressed'}</td>
                                                <td>{c.desired ? 'Not done → Done' : 'Done → Not done'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {applyError && <div className="odv3-rv-err">{applyError}</div>}
                        </div>
                        <div className="odv3-modal-a">
                            <button className="odv3-btn" disabled={applying} onClick={() => setShowReview(false)}>Back</button>
                            <button className="odv3-btn odv3-btn--primary" disabled={applying} onClick={applyPending}>
                                {applying ? 'Applying…' : `Apply ${pending.size} change${pending.size !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRefund && (
                <RefundModal
                    order={order}
                    storeKey={storeKey}
                    onClose={() => setShowRefund(false)}
                    onRefundIssued={() => { setShowRefund(false); refreshOrder(); }}
                />
            )}
            {reportOpen && (
                <ReportIssueModal
                    orderName={order?.name}
                    onSubmit={async (reason, note) => {
                        const numericId = String(orderId).split('/').pop();
                        const r = await _storeFetch(`/api/orders/${numericId}/report-issue?store=${encodeURIComponent(storeKey)}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ store_key: storeKey, reason, note }),
                        });
                        if (!r.ok) {
                            const d = await r.json().catch(() => ({}));
                            throw new Error(d.detail || `HTTP ${r.status}`);
                        }
                        setReportOpen(false);
                        refreshOrder();
                    }}
                    onCancel={() => setReportOpen(false)}
                />
            )}
            {preview && (
                <div className="odv3-ev-scrim" onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}>
                    <div className="odv3-ev-dlg" role="dialog" aria-modal="true" aria-labelledby="odv3-pv-title">
                        <div className="odv3-ev-h">
                            <h2 id="odv3-pv-title">{preview.title}</h2>
                            <button type="button" className="odv3-ev-x" aria-label="Close" onClick={() => setPreview(null)}>×</button>
                        </div>
                        <div className="odv3-ev-b">
                            <div className="odv3-lb-imgs">
                                {preview.imgs.length > 0 ? preview.imgs.map((im, i) => (
                                    <figure key={i}>
                                        <SmartProductImage src={im.src} alt={im.label || preview.title} size={400}
                                            style={{ width: '100%', height: 'auto', display: 'block' }}/>
                                        {im.label && <figcaption>{im.label}</figcaption>}
                                    </figure>
                                )) : <figure><img src={MISSING_IMAGE_SVG} alt="" style={{ width: '40%', opacity: .4 }}/></figure>}
                            </div>
                            {(preview.pressed || preview.badge) && (
                                <div className="odv3-lb-badges">
                                    {preview.badge && <span className={`odv3-pk odv3-pk--${preview.badge.cls}`}>{preview.badge.text}</span>}
                                    {preview.pressed && <span className="odv3-pk odv3-pk--pressed">Pressed</span>}
                                </div>
                            )}
                            <div className="odv3-lb-meta">
                                {[preview.sku && <code key="s" className="odv3-sku">{preview.sku}</code>, preview.variant, preview.price]
                                    .filter(Boolean)
                                    .map((x, i) => <React.Fragment key={i}>{i > 0 ? ' · ' : ''}{x}</React.Fragment>)}
                            </div>
                            {preview.comps.length > 0 && (
                                <div className="odv3-lb-comps">
                                    {preview.comps.map((c, i) => (
                                        <div key={i} className="odv3-lb-comp">
                                            {c.preview
                                                ? <div className="odv3-thumb odv3-thumb--one"><NumberPreviewThumb preview={c.preview.preview} number={c.preview.number} size={36} borderColor="#e5e7eb"/></div>
                                                : <div className="odv3-thumb odv3-thumb--one">
                                                    {c.img
                                                        ? <SmartProductImage src={c.img} alt={c.title} size={120} style={{ width: '100%', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', display: 'block' }}/>
                                                        : <img src={MISSING_IMAGE_SVG} alt="" style={{ width: '60%', opacity: .4 }}/>}
                                                </div>}
                                            <div>
                                                <div className="odv3-ev-t">{c.title}{c.chip && <> <span className="odv3-rnum">{c.chip}</span></>}{c.badge && <> <span className={`odv3-pk odv3-pk--${c.badge.cls}`}>{c.badge.text}</span></>}</div>
                                                {c.sku && <code className="odv3-sku">{c.sku}</code>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </FoMenuCtx.Provider>
        </V3Ctx.Provider>
        </SkuNotificationsContext.Provider>
        </StoreKeyContext.Provider>
    );
}
